import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Card,
  Button,
  Input,
  Select,
  Modal,
  Form,
  InputNumber,
  message,
  Space,
  Tooltip,
  Descriptions,
  Spin,
  Switch,
  Checkbox,
  Typography,
  Alert,
  Collapse,
  Upload,
  Timeline,
  Tag,
  Popconfirm,
  Radio,
} from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import FilterDrawer from '../components/FilterDrawer';
import NarrowExtra from '../components/NarrowExtra';
import { useNarrowScreen } from '../hooks/useNarrowScreen';
import { EditOutlined, UploadOutlined, WarningFilled } from '@ant-design/icons';
import ShipmentStatusCards from '../components/ShipmentStatusCards';
import dayjs from 'dayjs';
import {
  shipmentApi,
  preDeclarationApi,
  transportModeApi,
  channelApi,
  agentApi,
  customerApi,
  cartonApi,
  cartonBatchApi,
  type CartonBatchDTO,
  type ShipmentDTO,
  type ShipmentProblemLogDTO,
  type TransportModeDTO,
  type ChannelDTO,
  type AgentDTO,
  type CustomerDTO,
  type CustomerAddressDTO,
  type CustomerAddressSummaryDTO,
  type CartonDTO,
  type SubmitDepotContextDTO,
  type SubmitToDepotRequest,
  type SubmitToDepotResultDTO,
  type ShipmentInvoiceDTO,
  type ShipmentCustomsDocDTO,
  type TrackingDisplayDTO,
  type CheckAgentOrderResultDTO,
  type AgentCargoItemDTO,
  type SyncAgentDataResultDTO,
  type SyncTrackingDataResultDTO,
  type ManualMeasurementConfirmResultDTO,
  type ManualConfirmDeliveryResultDTO,
  type ManualTrackEventResultDTO,
  type ShipmentWeightConfirmationDTO,
  type ShipmentPackingListDTO,
} from '../api/kfc';
import CartonDetailDrawer from '../components/CartonDetailDrawer';
import ManualMeasurementModal from '../components/ManualMeasurementModal';
import ManualMeasurementHistory from '../components/ManualMeasurementHistory';
import CarrierLogo from '../components/CarrierLogo';
import WeightConfirmModal from '../components/WeightConfirmModal';
import WeightConfirmationHistory from '../components/WeightConfirmationHistory';
import TrackingDisplayPanel from '../components/TrackingDisplayPanel';
import ManualConfirmDeliveryModal from '../components/ManualConfirmDeliveryModal';
import ManualTrackEventModal from '../components/ManualTrackEventModal';
import ResponsiveDrawer from '../components/ResponsiveDrawer';
import AgentMeasurementTable from '../components/AgentMeasurementTable';
import CartonBatchFormModal from '../components/CartonBatchFormModal';
import AgentCargoBatchMatchModal, { autoAssignToBatches } from '../components/AgentCargoBatchMatchModal';
import {
  agentDisplayName,
  buildStructuredClientAddr,
  formatClientAddr,
  isIntelinkQ5Identifier,
  missingQ5ConsigneeFields,
  needsServiceChannelSelect,
} from '../utils/clientAddr';
import { clientTablePagination, serverTablePagination } from '../utils/tablePagination';
import { parseAgentMeasurementJson } from '../utils/agentMeasurementDisplay';
import { highlightText } from '../utils/highlightText';
import { ResizableTitle, useResizableColumns, type ResizableColumnType } from '../utils/useResizableColumns';
import { COUNTRY_LIST, formatCountryLabel, getCountryByCode } from '../constants/countries';

const REMARK_MAX_LENGTH = 500;
const CHANNEL_SHIPMENT_ID_MAX_LENGTH = 64;

const COUNTRY_SELECT_OPTIONS = COUNTRY_LIST.map((c) => ({
  label: formatCountryLabel(c),
  value: c.code,
}));

/** 承运商 logo + 官网轨迹查询链接 */
const CarrierTrackingLink: React.FC<{ shipment: ShipmentDTO; logoHeight?: number }> = ({ shipment, logoHeight = 16 }) => {
  const url = shipment.channelShipmentTrackingUrl;
  if (!url) return null;
  const carrier = shipment.carrierName || shipment.carrierCode || '承运商';
  return (
    <Tooltip title={`在 ${carrier} 官网查询轨迹`}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap', marginTop: 2 }}
      >
        <CarrierLogo code={shipment.carrierCode} name={shipment.carrierName} height={logoHeight} />
        <span>{carrier} 查询轨迹</span>
      </a>
    </Tooltip>
  );
};

/** 渠道.agentId 可能存 Mongo id 或 identifier */
function channelsForAgent(channels: ChannelDTO[], agents: AgentDTO[], agentMongoId?: string): ChannelDTO[] {
  if (!agentMongoId) return [];
  const agent = agents.find((a) => a.id === agentMongoId);
  const identifier = agent?.basicInfo?.identifier?.trim();
  return channels.filter((c) => {
    const ref = c.agentId?.trim();
    if (!ref) return false;
    return ref === agentMongoId || (!!identifier && ref === identifier);
  });
}

const agentLabel = (a: AgentDTO) => a.basicInfo?.name || a.basicInfo?.identifier || a.id || '-';

const channelDisplay = (s: { channelCode?: string; channelName?: string; channelId?: string }) => {
  if (s.channelCode || s.channelName) {
    return [s.channelCode, s.channelName].filter(Boolean).join(' - ');
  }
  return s.channelId || '-';
};
const statusLabel: Record<string, string> = {
  PRE_DECLARATION: '预申报',
  PENDING_HANDOVER: '预申报',
  SUBMITTED_TO_DEPOT: '已交代理仓',
  HANDOVER_CONFIRMING: '交货确认中',
  CONFIRMED_WAIT_DEPARTURE: '准备运输',
  IN_TRANSIT: '运输中',
  OUT_FOR_DELIVERY: '待派送',
  DELIVERED: '已签收',
  CANCELLED: '已撤销',
};

const cartonStateLabel: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

const agentInboundStatusLabel: Record<number, string> = {
  0: '新增',
  1: '已下单',
  2: '收货中',
  3: '已收货',
  4: '待确认',
  5: '已确认',
  6: '已删除',
  7: '调拨中',
  8: '扣货',
  9: '退货',
};
const agentOutboundStatusLabel: Record<number, string> = {
  0: '待出库',
  1: '出库中',
  2: '已出库',
};

const problemStatusLabel: Record<string, string> = {
  OPEN: '待处理',
  IN_PROGRESS: '处理中',
  RESOLVED: '已解除',
};

type ListRow = { type: 'shipment'; key: string; s: ShipmentDTO };

const ShipmentList: React.FC = () => {
  const narrow = useNarrowScreen();
  const [rows, setRows] = useState<ListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filterClientCodeInput, setFilterClientCodeInput] = useState('');
  const [filterProblemInput, setFilterProblemInput] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [clientCode, setClientCode] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [problemFilter, setProblemFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  // 列表顶部「按状态查看」手风琴：全局计数，不跟随筛选条件
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [statusTotal, setStatusTotal] = useState(0);
  const [filterShipmentIdInput, setFilterShipmentIdInput] = useState('');
  const [appliedShipmentIdKeyword, setAppliedShipmentIdKeyword] = useState('');
  const [filterAgentNameInput, setFilterAgentNameInput] = useState<string | undefined>();
  const [appliedAgentName, setAppliedAgentName] = useState<string | undefined>();
  const [weightModal, setWeightModal] = useState<ShipmentDTO | null>(null);
  const [weightHistoryKey, setWeightHistoryKey] = useState(0);
  const [submitDepotModal, setSubmitDepotModal] = useState<ShipmentDTO | null>(null);
  const [submitDepotContext, setSubmitDepotContext] = useState<SubmitDepotContextDTO | null>(null);
  const [submitDepotContextLoading, setSubmitDepotContextLoading] = useState(false);
  const [submitDepotForm] = Form.useForm<{
    depotId: string;
    hubInCode?: string;
    huolalaOrderNo?: string;
    photoUrls?: string;
    weig?: number;
    forceBind?: boolean;
  }>();
  const [submitDepotSubmitting, setSubmitDepotSubmitting] = useState(false);
  /** 本次交仓链路：用户已确认在无报关资料时继续 */
  const confirmWithoutCustomsDocsRef = useRef(false);
  const [syncConfirmModal, setSyncConfirmModal] = useState<{ shipment: ShipmentDTO; check: CheckAgentOrderResultDTO } | null>(null);
  const [syncConfirmSubmitting, setSyncConfirmSubmitting] = useState(false);
  const [syncCargoSelectedKeys, setSyncCargoSelectedKeys] = useState<React.Key[]>([]);
  const [checkAgentMiss, setCheckAgentMiss] = useState<CheckAgentOrderResultDTO | null>(null);
  const [updateRouteModal, setUpdateRouteModal] = useState<ShipmentDTO | null>(null);
  const [updateRouteForm] = Form.useForm<{
    agentId: string;
    transportMode: string;
    channelId?: string;
  }>();
  const [updateRouteSubmitting, setUpdateRouteSubmitting] = useState(false);
  const [remarkModal, setRemarkModal] = useState<ShipmentDTO | null>(null);
  const [remarkForm] = Form.useForm<{ remark?: string }>();
  const [remarkSubmitting, setRemarkSubmitting] = useState(false);
  const [pieceCountModal, setPieceCountModal] = useState<ShipmentDTO | null>(null);
  const [pieceCountForm] = Form.useForm<{ declaredPieceCount?: number }>();
  const [pieceCountSubmitting, setPieceCountSubmitting] = useState(false);
  const [channelShipmentModal, setChannelShipmentModal] = useState<ShipmentDTO | null>(null);
  const [channelShipmentForm] = Form.useForm<{ channelShipmentId?: string }>();
  const [channelShipmentSubmitting, setChannelShipmentSubmitting] = useState(false);
  const [countryModal, setCountryModal] = useState<ShipmentDTO | null>(null);
  const [countryForm] = Form.useForm<{ destinationCountryCode: string; destinationCountryName?: string }>();
  const [countrySubmitting, setCountrySubmitting] = useState(false);
  // 修改地址弹窗（仅预申报，限选该客户地址簿）
  const [addressModal, setAddressModal] = useState<ShipmentDTO | null>(null);
  const [addressForm] = Form.useForm<{ customerAddressId: string }>();
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [addrEditOptions, setAddrEditOptions] = useState<CustomerAddressSummaryDTO[]>([]);
  const [addrEditDetail, setAddrEditDetail] = useState<CustomerAddressDTO | null>(null);
  const [addrEditLoading, setAddrEditLoading] = useState(false);
  const [requiresCustomsModal, setRequiresCustomsModal] = useState<ShipmentDTO | null>(null);
  const [requiresCustomsForm] = Form.useForm<{ requiresCustoms: boolean }>();
  const [requiresCustomsSubmitting, setRequiresCustomsSubmitting] = useState(false);
  const [containsBatteryModal, setContainsBatteryModal] = useState<ShipmentDTO | null>(null);
  const [containsBatteryForm] = Form.useForm<{ containsBattery: boolean }>();
  const [containsBatterySubmitting, setContainsBatterySubmitting] = useState(false);
  const [manualShipmentId, setManualShipmentId] = useState(false);
  const [checkAgentOrderLoading, setCheckAgentOrderLoading] = useState(false);
  const [manualInboundVerifying, setManualInboundVerifying] = useState(false);
  const [manualAgentInboundNo, setManualAgentInboundNo] = useState('');
  const [syncAgentDataModal, setSyncAgentDataModal] = useState<ShipmentDTO | null>(null);
  const [syncTrackingDataModal, setSyncTrackingDataModal] = useState<ShipmentDTO | null>(null);
  const [syncAgentDataSubmitting, setSyncAgentDataSubmitting] = useState(false);
  const [syncTrackingDataSubmitting, setSyncTrackingDataSubmitting] = useState(false);
  const [syncAgentDataFromDrawerLoading, setSyncAgentDataFromDrawerLoading] = useState(false);
  const [manualMeasurementOpen, setManualMeasurementOpen] = useState(false);
  const [manualMeasurementRefreshKey, setManualMeasurementRefreshKey] = useState(0);
  const [resolveCargoSyncLoading, setResolveCargoSyncLoading] = useState(false);
  const [syncTrackingDataFromDrawerLoading, setSyncTrackingDataFromDrawerLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [bindModalOpen, setBindModalOpen] = useState(false);
  const [bindingShipment, setBindingShipment] = useState<ShipmentDTO | null>(null);
  const [batchesForBind, setBatchesForBind] = useState<CartonBatchDTO[]>([]);
  const [batchesForBindLoading, setBatchesForBindLoading] = useState(false);
  const [selectedBatchIds, setSelectedBatchIds] = useState<string[]>([]);
  const [bindBatchLoading, setBindBatchLoading] = useState(false);
  const [batchFormShipment, setBatchFormShipment] = useState<ShipmentDTO | null>(null);
  const [detailBatches, setDetailBatches] = useState<CartonBatchDTO[]>([]);
  const [detailPackingLists, setDetailPackingLists] = useState<ShipmentPackingListDTO[]>([]);
  const [detailPackingListsLoading, setDetailPackingListsLoading] = useState(false);
  const [packingListGenerating, setPackingListGenerating] = useState(false);
  const [packingListBatchGeneratingId, setPackingListBatchGeneratingId] = useState<string>();
  const [matchModal, setMatchModal] = useState<{ shipmentId: string } | null>(null);
  const [syncBatchAssign, setSyncBatchAssign] = useState<Record<number, string>>({});
  /** 运单详情滑窗 */
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailShipment, setDetailShipment] = useState<ShipmentDTO | null>(null);
  const [detailShipmentLoading, setDetailShipmentLoading] = useState(false);
  const [detailCartonsLoading, setDetailCartonsLoading] = useState(false);
  const [detailInvoicesLoading, setDetailInvoicesLoading] = useState(false);
  const detailReqSeqRef = useRef(0);
  const [detailCartons, setDetailCartons] = useState<CartonDTO[]>([]);
  const [detailInvoices, setDetailInvoices] = useState<ShipmentInvoiceDTO[]>([]);
  const [detailCustomsDocs, setDetailCustomsDocs] = useState<ShipmentCustomsDocDTO[]>([]);
  const [detailCustomsDocsLoading, setDetailCustomsDocsLoading] = useState(false);
  const [customsDocUploading, setCustomsDocUploading] = useState(false);
  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const [invoiceConfirmingId, setInvoiceConfirmingId] = useState<string | null>(null);
  const [detailProblemLogs, setDetailProblemLogs] = useState<ShipmentProblemLogDTO[]>([]);
  const [detailProblemLogsLoading, setDetailProblemLogsLoading] = useState(false);
  const [markProblemTarget, setMarkProblemTarget] = useState<ShipmentDTO | null>(null);
  const [markProblemForm] = Form.useForm<{ content: string }>();
  const [markProblemSubmitting, setMarkProblemSubmitting] = useState(false);
  const [addProblemLogOpen, setAddProblemLogOpen] = useState(false);
  const [addProblemLogForm] = Form.useForm<{ content: string }>();
  const [addProblemLogSubmitting, setAddProblemLogSubmitting] = useState(false);
  const [updateProblemStatusOpen, setUpdateProblemStatusOpen] = useState(false);
  const [updateProblemStatusForm] = Form.useForm<{ problemStatus: 'OPEN' | 'IN_PROGRESS'; content?: string }>();
  const [updateProblemStatusSubmitting, setUpdateProblemStatusSubmitting] = useState(false);
  const [resolveProblemOpen, setResolveProblemOpen] = useState(false);
  const [resolveProblemForm] = Form.useForm<{ content?: string; resolveReason?: 'REAL' | 'FALSE_POSITIVE' }>();
  const [resolveProblemSubmitting, setResolveProblemSubmitting] = useState(false);
  const [detailTrackingDisplay, setDetailTrackingDisplay] = useState<TrackingDisplayDTO | null>(null);
  const [detailTrackingDisplayLoading, setDetailTrackingDisplayLoading] = useState(false);
  const [manualConfirmDeliveryOpen, setManualConfirmDeliveryOpen] = useState(false);
  const [manualTrackOpen, setManualTrackOpen] = useState(false);
  const [manualTrackingSwitching, setManualTrackingSwitching] = useState(false);
  /** 详情侧滑内视图：运单 | 货物（单层，禁止嵌套 Drawer） */
  const [detailView, setDetailView] = useState<'shipment' | 'carton'>('shipment');
  const [cartonDetailId, setCartonDetailId] = useState<string | undefined>(undefined);
  const [transportModes, setTransportModes] = useState<TransportModeDTO[]>([]);
  const [channels, setChannels] = useState<ChannelDTO[]>([]);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [addresses, setAddresses] = useState<CustomerAddressSummaryDTO[]>([]);
  const [addressDetail, setAddressDetail] = useState<CustomerAddressDTO | null>(null);
  const [addressDetailLoading, setAddressDetailLoading] = useState(false);
  const [createForm] = Form.useForm();

  const load = () => {
    setLoading(true);
    shipmentApi
      .list({
        page,
        size: pageSize,
        clientCode,
        status: status || undefined,
        shipmentIdKeyword: appliedShipmentIdKeyword || undefined,
        problemFilter: problemFilter === 'ALL' ? undefined : problemFilter,
        agentName: appliedAgentName || undefined,
      })
      .then((res) => {
        const body = (res.data as any)?.data ?? res.data;
        const content = body?.content ?? [];
        const totalElements = body?.totalElements ?? 0;
        const shipRows: ListRow[] = (content as ShipmentDTO[]).map((s) => ({ type: 'shipment', key: `s-${s.shipmentId}`, s }));
        setRows(shipRows);
        setTotal(totalElements);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        setLoading(false);
        loadStatusCounts();
      });
  };

  /** 列表顶部手风琴的状态计数：全局口径，随每次列表加载刷新 */
  const loadStatusCounts = () => {
    shipmentApi
      .statusCounts()
      .then((res) => {
        setStatusCounts(res.data?.counts ?? {});
        setStatusTotal(res.data?.totalExcludingCancelled ?? 0);
      })
      .catch(() => {
        // 计数失败不影响主列表
      });
  };

  /** 状态卡片选择：立即生效；再点当前卡片取消筛选恢复全部 */
  const onStatusCardSelect = (v?: string) => {
    setStatus(v);
    setPage(0);
  };

  useEffect(() => {
    load();
  }, [page, pageSize, clientCode, status, appliedShipmentIdKeyword, problemFilter, appliedAgentName]);

  const applyFilters = () => {
    setAppliedShipmentIdKeyword(filterShipmentIdInput.trim());
    setClientCode(filterClientCodeInput.trim() || undefined);
    setProblemFilter(filterProblemInput);
    setAppliedAgentName(filterAgentNameInput?.trim() || undefined);
    setPage(0);
  };

  const resetFilters = () => {
    setFilterShipmentIdInput('');
    setFilterClientCodeInput('');
    setFilterProblemInput('ALL');
    setFilterAgentNameInput(undefined);
    setAppliedShipmentIdKeyword('');
    setClientCode(undefined);
    setStatus(undefined);
    setProblemFilter('ALL');
    setAppliedAgentName(undefined);
    setPage(0);
  };

  useEffect(() => {
    transportModeApi.list(true).then((res) => setTransportModes(res.data || [])).catch(() => setTransportModes([]));
    channelApi.list().then((res) => setChannels(res.data || [])).catch(() => setChannels([]));
    agentApi.list().then((res) => setAgents(res.data || [])).catch(() => setAgents([]));
    customerApi.list().then((res) => setCustomers(res.data || [])).catch(() => setCustomers([]));
  }, []);

  const onCreateAgentChange = (agentId: string) => {
    createForm.setFieldsValue({ channelId: undefined });
  };

  const onUpdateAgentChange = (agentId: string) => {
    updateRouteForm.setFieldsValue({ channelId: undefined });
  };

  const resolveDestinationCountryName = (
    code: string,
    nameFromForm?: string
  ): string | undefined => {
    const trimmedName = nameFromForm?.trim();
    if (trimmedName) return trimmedName;
    const fromMaster = getCountryByCode(code);
    return fromMaster?.nameZh || fromMaster?.nameEn || undefined;
  };

  const onCustomerChange = (customerCode: string) => {
    createForm.setFieldValue('clientAddr', undefined);
    setAddressDetail(null);
    if (!customerCode) {
      setAddresses([]);
      return;
    }
    customerApi
      .listAddressSummaries(customerCode)
      .then((res) => setAddresses(res.data || []))
      .catch(() => setAddresses([]));
  };

  const loadAddressDetail = (addressId?: string) => {
    if (!addressId) {
      message.warning('请先选择地址');
      return;
    }
    setAddressDetailLoading(true);
    customerApi
      .getAddressDetail(addressId)
      .then((res) => setAddressDetail(res.data))
      .catch(() => {
        setAddressDetail(null);
        message.error('加载地址详情失败');
      })
      .finally(() => setAddressDetailLoading(false));
  };

  const resolveAddressForSubmit = async (addressId: string): Promise<CustomerAddressDTO | null> => {
    if (addressDetail?.id === addressId) return addressDetail;
    try {
      const res = await customerApi.getAddressDetail(addressId);
      setAddressDetail(res.data);
      return res.data;
    } catch {
      message.error('加载地址详情失败');
      return null;
    }
  };

  const onFinishCreate = async (values: Record<string, unknown>) => {
    const addressId = values.clientAddr as string;
    const selectedAddr = await resolveAddressForSubmit(addressId);
    if (!selectedAddr) return;
    const clientAddrStr = buildStructuredClientAddr(selectedAddr);
    if (!selectedAddr.phone?.trim()) {
      message.warning('所选客户地址缺少电话，哲盟等代理交仓需要收件电话，请先完善地址簿');
      return;
    }
    const destinationCountryCode = (values.destinationCountryCode as string)?.trim();
    preDeclarationApi
      .create({
        clientCode: values.clientCode as string,
        clientAddr: clientAddrStr,
        transportMode: values.transportMode as string,
        channelId: (values.channelId as string | undefined)?.trim() || undefined,
        agentId: (values.agentId as string | undefined)?.trim() || undefined,
        destinationCountryCode,
        destinationCountryName: resolveDestinationCountryName(
          destinationCountryCode,
          values.destinationCountryName as string | undefined
        ),
        shipmentId: manualShipmentId ? ((values.shipmentId as string)?.trim() || undefined) : undefined,
        cargoDescJson: (values.cargoDescJson as string)?.trim() || '',
        customsDeclJson: undefined,
        declaredPieceCount: Number(values.declaredPieceCount),
        remark: (values.remark as string)?.trim() || undefined,
        requiresCustoms: values.requiresCustoms !== false,
        containsBattery: values.containsBattery === true,
      })
      .then((res) => {
        const sid = res.data?.shipmentId || res.data?.preDeclarationCode;
        if (sid) {
          Modal.success({
            title: '预申报创建成功',
            content: (
              <div>
                <div>
                  运单号：
                  <Typography.Text copyable={{ text: sid }} strong>
                    {sid}
                  </Typography.Text>
                </div>
                <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>
                  可复制后告知代理客服在代理系统建单，交仓时将自动查重同步
                </div>
              </div>
            ),
          });
        } else {
          message.success('预申报创建成功');
        }
        createForm.resetFields();
        setManualShipmentId(false);
        setAddressDetail(null);
        setCreateModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '创建失败'));
  };

  const customerIdOfShipment = (s: ShipmentDTO) => customers.find((c) => c.customerCode === s.clientCode)?.id;

  const loadBatchesForBind = (s: ShipmentDTO) => {
    const ids = [s.clientCode, customerIdOfShipment(s)].filter(Boolean) as string[];
    setBatchesForBindLoading(true);
    return cartonBatchApi
      .list({
        belongClientId: ids,
        forBind: true,
        bindShipmentId: s.shipmentId,
        size: 500,
      })
      .then((res) => {
        const list = res.data?.content || [];
        setBatchesForBind(list);
        return list;
      })
      .catch(() => {
        setBatchesForBind([]);
        return [] as CartonBatchDTO[];
      })
      .finally(() => setBatchesForBindLoading(false));
  };

  const openBindModal = (s: ShipmentDTO) => {
    setBindingShipment(s);
    setSelectedBatchIds([]);
    setBindModalOpen(true);
    loadBatchesForBind(s).then((list) => {
      setSelectedBatchIds(list.filter((b) => b.shipmentId === s.shipmentId && b.id).map((b) => b.id!));
    });
  };

  const loadDetailBatches = (shipmentId: string) =>
    cartonBatchApi
      .list({ shipmentId, size: 500 })
      .then((res) => setDetailBatches(res.data?.content || []))
      .catch(() => setDetailBatches([]));

  const loadDetailPackingLists = (shipmentId: string) => {
    setDetailPackingListsLoading(true);
    shipmentApi
      .listPackingLists(shipmentId)
      .then((res) => setDetailPackingLists(Array.isArray(res.data) ? res.data : []))
      .catch(() => setDetailPackingLists([]))
      .finally(() => setDetailPackingListsLoading(false));
  };

  const onDownloadPackingList = (shipmentId: string, rec: ShipmentPackingListDTO) => {
    shipmentApi
      .downloadPackingList(shipmentId, rec.id)
      .then((res) => {
        const blob = res.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = rec.fileName || 'packing-list.xls';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => message.error(e.response?.data?.message || '下载失败'));
  };

  const onGeneratePackingList = (shipmentId: string) => {
    setPackingListGenerating(true);
    shipmentApi
      .generatePackingList(shipmentId)
      .then((res) => {
        message.success('随货清单已生成');
        loadDetailPackingLists(shipmentId);
        onDownloadPackingList(shipmentId, res.data);
      })
      .catch((e) => message.error(e.response?.data?.message || '生成失败'))
      .finally(() => setPackingListGenerating(false));
  };

  const onGenerateBatchPackingList = (shipmentId: string, batchId: string) => {
    setPackingListBatchGeneratingId(batchId);
    shipmentApi
      .generateBatchPackingList(shipmentId, batchId)
      .then((res) => {
        message.success('批次随货清单已生成');
        loadDetailPackingLists(shipmentId);
        onDownloadPackingList(shipmentId, res.data);
      })
      .catch((e) => message.error(e.response?.data?.message || '生成失败'))
      .finally(() => setPackingListBatchGeneratingId(undefined));
  };

  const refreshShipmentAfterBatchChange = (shipmentId: string) => {
    load();
    if (detailDrawerOpen && detailShipment?.shipmentId === shipmentId) {
      shipmentApi.get(shipmentId).then((r) => setDetailShipment(r.data));
      loadDetailBatches(shipmentId);
      loadDetailPackingLists(shipmentId);
      cartonApi.list({ shipmentId }).then((res) => setDetailCartons(cartonApi.unwrapList(res.data)));
    }
  };

  const onSaveBatchBinding = () => {
    if (!bindingShipment) return;
    const shipmentId = bindingShipment.shipmentId;
    const boundNow = batchesForBind.filter((b) => b.shipmentId === shipmentId && b.id).map((b) => b.id!);
    const toBind = selectedBatchIds.filter((id) => !boundNow.includes(id));
    const toUnbind = boundNow.filter((id) => !selectedBatchIds.includes(id));
    if (toBind.length === 0 && toUnbind.length === 0) {
      setBindModalOpen(false);
      setBindingShipment(null);
      return;
    }
    setBindBatchLoading(true);
    const run = async () => {
      if (toUnbind.length) await cartonBatchApi.unbindShipment({ batchIds: toUnbind });
      if (toBind.length) await cartonBatchApi.bindShipment({ batchIds: toBind, shipmentId });
    };
    run()
      .then(() => {
        message.success(`已绑定 ${toBind.length} 批${toUnbind.length ? `，解绑 ${toUnbind.length} 批` : ''}`);
        setBindModalOpen(false);
        setBindingShipment(null);
        refreshShipmentAfterBatchChange(shipmentId);
      })
      .catch((e) => message.error(e.response?.data?.message || '绑定失败'))
      .finally(() => setBindBatchLoading(false));
  };

  const unbindOneBatch = (shipmentId: string, batch: CartonBatchDTO) => {
    if (!batch.id) return;
    cartonBatchApi
      .unbindShipment({ batchIds: [batch.id] })
      .then(() => {
        message.success(`已解绑批次 ${batch.batchId}`);
        refreshShipmentAfterBatchChange(shipmentId);
      })
      .catch((e) => message.error(e.response?.data?.message || '解绑失败'));
  };

  const onBatchCreatedForShipment = (shipmentId: string) => {
    setBatchFormShipment(null);
    refreshShipmentAfterBatchChange(shipmentId);
    if (bindModalOpen && bindingShipment?.shipmentId === shipmentId) {
      loadBatchesForBind(bindingShipment).then((list) => {
        const bound = list.filter((b) => b.shipmentId === shipmentId && b.id).map((b) => b.id!);
        setSelectedBatchIds((prev) => Array.from(new Set([...prev, ...bound])));
      });
    }
  };

  const nextBatchIdFor = (s: ShipmentDTO) => {
    const n = Math.max(
      s.boundBatchCount ?? 0,
      detailShipment?.shipmentId === s.shipmentId ? detailBatches.length : 0,
      bindingShipment?.shipmentId === s.shipmentId
        ? batchesForBind.filter((b) => b.shipmentId === s.shipmentId).length
        : 0,
    );
    return `${s.shipmentId}-B${n + 1}`;
  };

  const openDetailDrawer = (s: ShipmentDTO) => {
    const reqId = ++detailReqSeqRef.current;
    setDetailShipment(null);
    setDetailCartons([]);
    setDetailBatches([]);
    setDetailInvoices([]);
    setDetailCustomsDocs([]);
    setDetailProblemLogs([]);
    setDetailTrackingDisplay(null);
    setDetailView('shipment');
    setCartonDetailId(undefined);
    setDetailDrawerOpen(true);
    setDetailShipmentLoading(true);
    setDetailCartonsLoading(true);
    setDetailInvoicesLoading(true);
    setDetailCustomsDocsLoading(true);
    setDetailProblemLogsLoading(true);

    shipmentApi
      .get(s.shipmentId)
      .then((shipRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailShipment(shipRes.data);
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载运单信息失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailShipmentLoading(false);
      });

    cartonBatchApi
      .list({ shipmentId: s.shipmentId, size: 500 })
      .then((res) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailBatches(res.data?.content || []);
      })
      .catch(() => {});

    cartonApi
      .list({ shipmentId: s.shipmentId })
      .then((cartonRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailCartons(cartonApi.unwrapList(cartonRes.data));
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载绑定货物失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailCartonsLoading(false);
      });

    shipmentApi
      .listInvoices(s.shipmentId)
      .then((invRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailInvoices(Array.isArray(invRes.data) ? invRes.data : []);
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载发票列表失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailInvoicesLoading(false);
      });

    shipmentApi
      .listCustomsDocs(s.shipmentId)
      .then((docRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailCustomsDocs(Array.isArray(docRes.data) ? docRes.data : []);
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载报关资料失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailCustomsDocsLoading(false);
      });

    shipmentApi
      .listProblemLogs(s.shipmentId)
      .then((logRes) => {
        if (reqId !== detailReqSeqRef.current) return;
        setDetailProblemLogs(Array.isArray(logRes.data) ? logRes.data : []);
      })
      .catch(() => {
        if (reqId !== detailReqSeqRef.current) return;
        message.warning('加载问题件进展失败');
      })
      .finally(() => {
        if (reqId === detailReqSeqRef.current) setDetailProblemLogsLoading(false);
      });
  };

  const refreshDetailCustomsDocs = () => {
    if (!detailShipment?.shipmentId) return;
    shipmentApi.listCustomsDocs(detailShipment.shipmentId).then((res) => {
      setDetailCustomsDocs(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  };

  const refreshDetailProblem = () => {
    if (!detailShipment?.shipmentId) return;
    const sid = detailShipment.shipmentId;
    shipmentApi.get(sid).then((r) => setDetailShipment(r.data)).catch(() => {});
    shipmentApi.listProblemLogs(sid).then((res) => {
      setDetailProblemLogs(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
  };

  const closeDetailDrawer = () => {
    detailReqSeqRef.current += 1;
    setDetailDrawerOpen(false);
    setDetailView('shipment');
    setCartonDetailId(undefined);
  };

  const openCartonInDetail = (cartonId: string) => {
    setCartonDetailId(cartonId);
    setDetailView('carton');
  };

  const backToShipmentDetail = () => {
    setDetailView('shipment');
    setCartonDetailId(undefined);
  };

  /** 当详情抽屉打开且运单有代理入库单号或已手动确认签收时，拉取轨迹展示数据 */
  useEffect(() => {
    if (!detailDrawerOpen || !detailShipment?.shipmentId
        || (!detailShipment?.agentInboundNo?.trim()
          && !detailShipment?.manualDeliveryConfirmedAt
          && !detailShipment?.manualTrackingEnabled
          && !detailShipment?.hasManualTrackEvents)) {
      if (!detailDrawerOpen) setDetailTrackingDisplay(null);
      setDetailTrackingDisplayLoading(false);
      return;
    }
    setDetailTrackingDisplayLoading(true);
    shipmentApi
      .getTrackingDisplay(detailShipment.shipmentId)
      .then((res) => setDetailTrackingDisplay(res.data ?? null))
      .catch(() => setDetailTrackingDisplay(null))
      .finally(() => setDetailTrackingDisplayLoading(false));
  }, [detailDrawerOpen, detailShipment?.shipmentId, detailShipment?.agentInboundNo, detailShipment?.manualDeliveryConfirmedAt, detailShipment?.manualTrackingEnabled, detailShipment?.hasManualTrackEvents]);

  const openCancelShipment = (s: ShipmentDTO) => {
    let reason = '';
    const cartonCount = detailCartons.length;
    Modal.confirm({
      title: `撤销运单 ${s.shipmentId}？`,
      okText: '确认撤销',
      okButtonProps: { danger: true },
      cancelText: '取消',
      content: (
        <div>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            撤销后运单状态变为「已撤销」，不可编辑；可通过「重新申报」恢复为预申报。
            {cartonCount > 0 && (
              <Typography.Text type="warning">
                {` 已绑定的 ${cartonCount} 箱货物将被解绑，重新申报后需重新绑定。`}
              </Typography.Text>
            )}
          </Typography.Paragraph>
          <Input.TextArea
            rows={3}
            maxLength={200}
            showCount
            placeholder="撤销原因（选填）"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      onOk: () =>
        shipmentApi
          .cancelShipment(s.shipmentId, reason.trim() || undefined)
          .then((res) => {
            message.success('运单已撤销');
            setDetailShipment(res.data);
            refreshDetailCartons();
            load();
          })
          .catch((e) => {
            message.error(e.response?.data?.message || '撤销失败');
            return Promise.reject(e);
          }),
    });
  };

  const openRedeclareShipment = (s: ShipmentDTO) => {
    Modal.confirm({
      title: `重新申报运单 ${s.shipmentId}？`,
      content: '运单将恢复为「预申报」状态，撤销时解绑的货物需重新绑定。',
      okText: '确认重新申报',
      cancelText: '取消',
      onOk: () =>
        shipmentApi
          .redeclareShipment(s.shipmentId)
          .then((res) => {
            message.success('已重新申报');
            setDetailShipment(res.data);
            load();
          })
          .catch((e) => {
            message.error(e.response?.data?.message || '重新申报失败');
            return Promise.reject(e);
          }),
    });
  };

  const refreshDetailInvoices = () => {
    if (!detailShipment?.shipmentId) return;
    shipmentApi.listInvoices(detailShipment.shipmentId).then((res) => {
      setDetailInvoices(Array.isArray(res.data) ? res.data : []);
    });
  };

  const refreshDetailCartons = () => {
    if (!detailShipment?.shipmentId) return;
    loadDetailBatches(detailShipment.shipmentId);
    loadDetailPackingLists(detailShipment.shipmentId);
    setDetailCartonsLoading(true);
    cartonApi
      .list({ shipmentId: detailShipment.shipmentId })
      .then((res) => {
        setDetailCartons(cartonApi.unwrapList(res.data));
      })
      .finally(() => setDetailCartonsLoading(false));
  };

  const onWeightConfirmed = (record: ShipmentWeightConfirmationDTO) => {
    setWeightModal(null);
    load();
    if (detailShipment?.shipmentId === record.shipmentId) {
      shipmentApi.get(record.shipmentId).then((r) => setDetailShipment(r.data)).catch(() => {});
      setWeightHistoryKey((k) => k + 1);
    }
  };

  const onCalculateBillingWeight = (s: ShipmentDTO) => {
    shipmentApi
      .calculateBillingWeight(s.shipmentId)
      .then((res) => {
        const d = res.data;
        if (d.missingInput) {
          message.warning('缺少入仓箱测量数据，无法计算');
        } else {
          message.success(`已计算建议计费重 ${d.suggestedWeightKg ?? '-'} kg`);
        }
        load();
        if (detailShipment?.shipmentId === s.shipmentId) {
          shipmentApi.get(s.shipmentId).then((r) => setDetailShipment(r.data)).catch(() => {});
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '计算失败'));
  };

  const parseBillingProcessSteps = (json?: string): string[] => {
    if (!json) return [];
    try {
      const v = JSON.parse(json);
      return Array.isArray(v) ? v.map(String) : [String(json)];
    } catch {
      return [json];
    }
  };

  const openCreateDepotFlow = (s: ShipmentDTO, checkMiss?: CheckAgentOrderResultDTO | null) => {
    setSubmitDepotModal(s);
    setSubmitDepotContext(null);
    setCheckAgentMiss(checkMiss || null);
    submitDepotForm.resetFields();
    setSubmitDepotContextLoading(true);
    shipmentApi
      .getSubmitDepotContext(s.shipmentId)
      .then((res) => {
        setSubmitDepotContext(res.data);
        if (res.data?.suggestedWeig != null) {
          submitDepotForm.setFieldsValue({ weig: res.data.suggestedWeig });
        }
        const firstHub = (res.data?.serviceChannels || []).find((c) => c.serviceCode)?.serviceCode;
        if (firstHub) {
          submitDepotForm.setFieldsValue({ hubInCode: firstHub });
        }
      })
      .catch((e) => {
        message.error(e.response?.data?.message || '获取代理信息失败');
        setSubmitDepotModal(null);
        setCheckAgentMiss(null);
      })
      .finally(() => setSubmitDepotContextLoading(false));
  };

  const openUpdateLogistics = (s: ShipmentDTO) => {
    setUpdateRouteModal(s);
    updateRouteForm.setFieldsValue({
      agentId: s.agentId,
      transportMode: s.transportMode,
      channelId: s.channelId,
    });
  };

  /** 交代理仓：先校验代理/渠道/目的国家，再查代理是否已有单；有则对比确认同步，无则走创建订单弹窗 */
  const proceedSubmitDepotCheck = (s: ShipmentDTO) => {
    setCheckAgentOrderLoading(true);
    shipmentApi
      .checkAgentOrder(s.shipmentId)
      .then((res) => {
        const check = res.data;
        if (check?.exists) {
          const cargo = check.cargoItems || [];
          setSyncCargoSelectedKeys(cargo.map((c: AgentCargoItemDTO, i: number) => c.sourceIndex ?? i));
          setSyncBatchAssign(autoAssignToBatches(cargo, check.boundBatches || []));
          setSyncConfirmModal({ shipment: s, check });
        } else {
          if (check?.message) {
            message.warning(check.message);
          }
          openCreateDepotFlow(s, check);
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '查询代理运单失败'))
      .finally(() => setCheckAgentOrderLoading(false));
  };

  const openSubmitDepotModal = (s: ShipmentDTO) => {
    if (!s.agentId || !s.channelId || !(s.destinationCountryCode || s.destinationCountry)) {
      message.warning('请先完善代理、渠道与目的国家');
      openUpdateLogistics(s);
      return;
    }
    confirmWithoutCustomsDocsRef.current = false;
    const requiresCustoms = s.requiresCustoms !== false;
    const docCount = s.customsDocCount ?? 0;
    if (requiresCustoms && docCount <= 0) {
      Modal.confirm({
        title: '尚未上传报关资料',
        content: '该运单需要报关但尚未上传报关资料，是否确认提交代理仓？',
        okText: '确认提交',
        cancelText: '去上传',
        onOk: () => {
          confirmWithoutCustomsDocsRef.current = true;
          proceedSubmitDepotCheck(s);
        },
        onCancel: () => {
          openDetailDrawer(s);
        },
      });
      return;
    }
    proceedSubmitDepotCheck(s);
  };

  const onSyncExistingOk = () => {
    if (!syncConfirmModal) return;
    const { shipment } = syncConfirmModal;
    const selectedIndexes = syncCargoSelectedKeys
      .map((k) => Number(k))
      .filter((n) => !Number.isNaN(n));
    const syncCargo = selectedIndexes.length > 0;
    const hasBatches = (syncConfirmModal.check.boundBatches?.length ?? 0) > 0;
    const cargoBatchAssignments = hasBatches
      ? selectedIndexes
        .filter((i) => syncBatchAssign[i])
        .map((i) => ({ sourceIndex: i, batchId: syncBatchAssign[i] }))
      : undefined;
    setSyncConfirmSubmitting(true);
    shipmentApi
      .submitToAgentDepot(shipment.shipmentId, {
        syncExisting: true,
        syncCargo,
        selectedCargoIndexes: syncCargo ? selectedIndexes : [],
        agentInboundNo: syncConfirmModal.check.agentInboundNo || undefined,
        cargoBatchAssignments,
        updateBatchPieceCount: hasBatches ? true : undefined,
        confirmWithoutCustomsDocs: confirmWithoutCustomsDocsRef.current || undefined,
      })
      .then((res) => {
        const data: SubmitToDepotResultDTO | undefined = res.data;
        const created = data?.createdCartons?.length ?? 0;
        const warnings = data?.warnings || [];
        if (created > 0) {
          message.success(`已同步代理运单并创建 ${created} 箱货物`);
        } else {
          message.success(data?.message || '已同步代理运单并完成交仓');
        }
        if (warnings.length) {
          message.warning(warnings.slice(0, 3).join('；'));
        }
        setSyncConfirmModal(null);
        setSyncCargoSelectedKeys([]);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === shipment.shipmentId) {
          shipmentApi.get(shipment.shipmentId).then((r) => setDetailShipment(r.data));
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncConfirmSubmitting(false));
  };

  const onUpdateRouteOk = () => {
    const s = updateRouteModal!;
    updateRouteForm.validateFields().then((values) => {
      setUpdateRouteSubmitting(true);
      shipmentApi
        .updateLogistics(s.shipmentId, {
          transportMode: values.transportMode,
          channelId: values.channelId || null,
          agentId: values.agentId,
        })
        .then(() => {
          message.success('运输方式已更新');
          setUpdateRouteModal(null);
          updateRouteForm.resetFields();
          load();
          if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
            shipmentApi.get(s.shipmentId).then((res) => setDetailShipment(res.data));
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '更新失败'))
        .finally(() => setUpdateRouteSubmitting(false));
    });
  };

  const onSubmitDepotOk = () => {
    const s = submitDepotModal!;
    const agentId = submitDepotContext?.agent?.basicInfo?.identifier;
    const forceBind = !!submitDepotForm.getFieldValue('forceBind');
    if (forceBind && isIntelinkQ5Identifier(agentId)) {
      setSubmitDepotSubmitting(true);
      shipmentApi
        .submitToAgentDepot(s.shipmentId, {
          syncExisting: true,
          forceBind: true,
          confirmWithoutCustomsDocs: confirmWithoutCustomsDocsRef.current || undefined,
        })
        .then(() => {
          message.success('已强制绑定代理运单并完成交仓');
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          setCheckAgentMiss(null);
          submitDepotForm.resetFields();
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '强制绑定失败'))
        .finally(() => setSubmitDepotSubmitting(false));
      return;
    }
    if (needsServiceChannelSelect(agentId)) {
      const missing = missingQ5ConsigneeFields(s.clientAddr, s.clientCode);
      if (missing.length) {
        message.warning(
          `${agentDisplayName(agentId)} 交仓缺少：${missing.join('、')}。请完善客户地址（含联系人电话）后重试，或更新运单地址为结构化信息。`
        );
        return;
      }
    }
    submitDepotForm.validateFields().then((values) => {
      const body: SubmitToDepotRequest = {
        depotId: values.depotId,
        hubInCode: values.hubInCode?.trim() || undefined,
        huolalaOrderNo: values.huolalaOrderNo?.trim() || undefined,
        photoUrls: values.photoUrls ? values.photoUrls.trim().split(/\s+/).filter(Boolean) : undefined,
        weig: values.weig != null && Number(values.weig) > 0 ? Number(values.weig) : undefined,
        confirmWithoutCustomsDocs: confirmWithoutCustomsDocsRef.current || undefined,
      };
      setSubmitDepotSubmitting(true);
      shipmentApi
        .submitToAgentDepot(s.shipmentId, body)
        .then(() => {
          message.success('已提交代理仓');
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          setCheckAgentMiss(null);
          submitDepotForm.resetFields();
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'))
        .finally(() => setSubmitDepotSubmitting(false));
    });
  };

  const onUploadInvoice = (file: File) => {
    if (!detailShipment?.shipmentId) return false;
    setInvoiceUploading(true);
    shipmentApi
      .uploadInvoice(detailShipment.shipmentId, file)
      .then(() => {
        message.success('发票已上传');
        refreshDetailInvoices();
      })
      .catch((e) => message.error(e.response?.data?.message || '上传发票失败'))
      .finally(() => setInvoiceUploading(false));
    return false;
  };

  const onDownloadInvoice = (shipmentId: string, inv: ShipmentInvoiceDTO) => {
    shipmentApi
      .downloadInvoice(shipmentId, inv.id)
      .then((res) => {
        const blob = res.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = inv.fileName || 'invoice.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => message.error(e.response?.data?.message || '下载失败'));
  };

  const onConfirmValidInvoice = (inv: ShipmentInvoiceDTO) => {
    if (!detailShipment?.shipmentId || !inv.id) return;
    Modal.confirm({
      title: '确认为有效发票',
      content: (
        <p>
          确认将 <strong>{inv.fileName || inv.id}</strong> 设为有效发票？同运单其他发票将标为无效。
        </p>
      ),
      okText: '确认',
      onOk: () => {
        setInvoiceConfirmingId(inv.id);
        return shipmentApi
          .confirmValidInvoice(detailShipment.shipmentId, inv.id)
          .then(() => {
            message.success('已确认为有效发票');
            refreshDetailInvoices();
          })
          .catch((e) => {
            message.error(e.response?.data?.message || '确认失败');
            throw e;
          })
          .finally(() => setInvoiceConfirmingId(null));
      },
    });
  };

  const onSyncTrackingDataOk = () => {
    const s = syncTrackingDataModal!;
    setSyncTrackingDataSubmitting(true);
    shipmentApi
      .syncTrackingData(s.shipmentId)
      .then((res) => {
        const data = res.data as SyncTrackingDataResultDTO;
        if (data?.statusAdvanced) {
          message.success(data.message || `轨迹已同步，状态已更新为 ${statusLabel[data.newStatus || ''] || data.newStatus}`);
        } else {
          message.success(data?.message || '已同步轨迹');
        }
        setSyncTrackingDataModal(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
          shipmentApi.get(s.shipmentId).then((r) => setDetailShipment(r.data));
          shipmentApi.getTrackingDisplay(s.shipmentId).then((r) => setDetailTrackingDisplay(r.data ?? null)).catch(() => setDetailTrackingDisplay(null));
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncTrackingDataSubmitting(false));
  };

  /** 详情抽屉内同步轨迹：拉代理轨迹并按推断推进状态 */
  const syncTrackingDataFromDrawer = () => {
    if (!detailShipment?.shipmentId || !detailShipment.agentInboundNo?.trim()) {
      message.warning('当前运单无代理入库单号，请先执行「交代理仓」');
      return;
    }
    if (detailShipment.status === 'DELIVERED') {
      message.warning('运单已签收，无需同步轨迹');
      return;
    }
    setSyncTrackingDataFromDrawerLoading(true);
    shipmentApi
      .syncTrackingData(detailShipment.shipmentId)
      .then((res) => {
        const data = res.data as SyncTrackingDataResultDTO;
        if (data?.statusAdvanced) {
          message.success(data.message || `轨迹已同步，状态已更新为 ${statusLabel[data.newStatus || ''] || data.newStatus}`);
        } else {
          message.success(data?.message || '已同步轨迹');
        }
        return shipmentApi.get(detailShipment.shipmentId).then((r) => {
          setDetailShipment(r.data);
          load();
          return shipmentApi.getTrackingDisplay(detailShipment.shipmentId).then((tr) => setDetailTrackingDisplay(tr.data ?? null)).catch(() => setDetailTrackingDisplay(null));
        });
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncTrackingDataFromDrawerLoading(false));
  };

  const canManualConfirmDelivery = (s: ShipmentDTO) =>
    ['CONFIRMED_WAIT_DEPARTURE', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(String(s?.status ?? '').trim().toUpperCase());

  const refreshDetailAfterTrack = (shipmentId: string) => {
    shipmentApi.get(shipmentId).then((r) => {
      setDetailShipment(r.data);
      load();
    });
    shipmentApi
      .getTrackingDisplay(shipmentId)
      .then((tr) => setDetailTrackingDisplay(tr.data ?? null))
      .catch(() => setDetailTrackingDisplay(null));
  };

  const onManualTrackingSwitch = (enabled: boolean) => {
    if (!detailShipment || isShipmentLocked(detailShipment)) return;
    setManualTrackingSwitching(true);
    shipmentApi
      .setManualTrackingEnabled(detailShipment.shipmentId, enabled)
      .then((r) => {
        setDetailShipment(r.data);
        message.success(enabled ? '已开启手动轨迹，不再同步代理' : '已关闭手动轨迹，将恢复代理同步');
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setManualTrackingSwitching(false));
  };

  const onManualTrackSubmitted = (_result: ManualTrackEventResultDTO) => {
    setManualTrackOpen(false);
    if (!detailShipment) return;
    refreshDetailAfterTrack(detailShipment.shipmentId);
  };

  const onManualConfirmDelivery = (result: ManualConfirmDeliveryResultDTO) => {
    setManualConfirmDeliveryOpen(false);
    if (!detailShipment) return;
    shipmentApi.get(detailShipment.shipmentId).then((r) => {
      setDetailShipment(r.data);
      load();
    });
    shipmentApi
      .getTrackingDisplay(detailShipment.shipmentId)
      .then((tr) => setDetailTrackingDisplay(tr.data ?? null))
      .catch(() => setDetailTrackingDisplay(null));
    if (result.problemResolved) {
      refreshDetailProblem();
    }
  };

  const onSyncAgentDataOk = () => {
    const s = syncAgentDataModal!;
    setSyncAgentDataSubmitting(true);
    shipmentApi
      .syncAgentData(s.shipmentId)
      .then((res) => {
        const data = res.data as SyncAgentDataResultDTO;
        setSyncAgentDataModal(null);
        return handleSyncAgentDataResult(s.shipmentId, data);
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncAgentDataSubmitting(false));
  };

  /** 同步后若箱数不一致：弹窗对比运单箱数 vs 代理箱数，确认后按代理数据更新并写操作日志 */
  const handleSyncAgentDataResult = (shipmentId: string, data: SyncAgentDataResultDTO) => {
    const refreshAfter = () => {
      load();
      if (detailDrawerOpen && detailShipment?.shipmentId === shipmentId) {
        return shipmentApi.get(shipmentId).then((r) => {
          setDetailShipment(r.data);
          refreshDetailCartons();
        });
      }
      return Promise.resolve();
    };

    if (!data?.cargoSyncPending) {
      message.success(data?.message || '已同步代理入仓数据');
      return refreshAfter();
    }

    const kfyN = data.kfyCartonCount ?? 0;
    const agentN = data.agentCargoCount ?? 0;
    if ((data.boundBatches?.length ?? 0) > 0) {
      return new Promise<void>((resolve) => {
        Modal.confirm({
          title: '件数不一致，是否匹配到货物批次？',
          zIndex: 1100,
          content: (
            <div>
              <p style={{ marginBottom: 4 }}>运单批次件数：<strong>{kfyN}</strong></p>
              <p style={{ marginBottom: 8 }}>代理入仓箱数：<strong>{agentN}</strong></p>
              <p style={{ color: '#666', marginBottom: 0 }}>
                点「匹配到批次」为代理箱选择归属批次并生成单箱；点「稍后处理」可稍后在详情中处理。
              </p>
            </div>
          ),
          okText: '匹配到批次',
          cancelText: '稍后处理',
          onOk: () => {
            setMatchModal({ shipmentId });
            refreshAfter().finally(() => resolve());
          },
          onCancel: () => {
            shipmentApi
              .resolveCargoSync(shipmentId, { mode: 'LATER' })
              .then(() => message.info('已同步；件数不一致，可稍后在详情处理'))
              .catch(() => message.warning(data.message || '已同步；件数不一致，请稍后在详情处理'))
              .finally(() => {
                refreshAfter().finally(() => resolve());
              });
          },
        });
      });
    }
    return new Promise<void>((resolve) => {
      Modal.confirm({
        title: '箱数不一致，是否按代理数据更新？',
        zIndex: 1100,
        content: (
          <div>
            <p style={{ marginBottom: 8 }}>已同步代理入仓状态与测量数据，但箱数不一致：</p>
            <p style={{ marginBottom: 4 }}>
              运单箱数：<strong>{kfyN}</strong>
            </p>
            <p style={{ marginBottom: 8 }}>
              代理箱数：<strong>{agentN}</strong>
            </p>
            <p style={{ color: '#666', marginBottom: 0 }}>
              点「更新」将按代理测量清单替换当前运单绑定货物，并记录操作日志；点「稍后处理」可稍后在详情中处理。
            </p>
          </div>
        ),
        okText: '更新',
        cancelText: '稍后处理',
        onOk: () =>
          shipmentApi
            .resolveCargoSync(shipmentId, { mode: 'REPLACE' })
            .then((res) => {
              const body = res.data;
              if (body?.warnings?.length) {
                message.warning(body.message || '已更新，请查看警告');
                Modal.info({
                  title: '更新结果',
                  zIndex: 1100,
                  content: (
                    <div>
                      <p>{body.message}</p>
                      <ul style={{ paddingLeft: 20, margin: 0 }}>
                        {body.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  ),
                });
              } else {
                message.success(body?.message || '已按代理数据更新货物箱');
              }
              return refreshAfter();
            })
            .catch((e) => {
              message.error(e.response?.data?.message || '更新失败');
              return refreshAfter();
            })
            .finally(() => resolve()),
        onCancel: () => {
          shipmentApi
            .resolveCargoSync(shipmentId, { mode: 'LATER' })
            .then(() => message.info('已同步；箱数不一致，可稍后在详情处理'))
            .catch(() => message.warning(data.message || '已同步；箱数不一致，请稍后在详情处理'))
            .finally(() => {
              refreshAfter().finally(() => resolve());
            });
        },
      });
    });
  };

  /** 在详情抽屉内点击「同步代理入仓数据」：同步成功后刷新当前详情 */
  const syncAgentDataFromDrawer = () => {
    if (!detailShipment?.shipmentId || !detailShipment.agentInboundNo?.trim()) {
      message.warning('当前运单无代理入库单号，请先执行「交代理仓」');
      return;
    }
    setSyncAgentDataFromDrawerLoading(true);
    const sid = detailShipment.shipmentId;
    shipmentApi
      .syncAgentData(sid)
      .then((res) => handleSyncAgentDataResult(sid, res.data as SyncAgentDataResultDTO))
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncAgentDataFromDrawerLoading(false));
  };

  /** 手动上传测量数据确认后：复用同步结果处理（箱数不一致弹窗 + 刷新），并提示计费重结果 */
  const onManualMeasurementConfirmed = (shipmentId: string, data: ManualMeasurementConfirmResultDTO) => {
    setManualMeasurementRefreshKey((k) => k + 1);
    handleSyncAgentDataResult(shipmentId, data);
    if (data.billingMessage) {
      message.warning(data.billingMessage);
    } else if (data.billingSuggestedKg != null) {
      message.info(`计费重已重算：建议 ${data.billingSuggestedKg} kg`);
    }
  };

  const resolveCargoSync = (mode: 'REPLACE' | 'MERGE' | 'LATER') => {
    if (!detailShipment?.shipmentId) return Promise.resolve();
    setResolveCargoSyncLoading(true);
    return shipmentApi
      .resolveCargoSync(detailShipment.shipmentId, { mode })
      .then((res) => {
        const data = res.data;
        if (data?.warnings?.length) {
          message.warning(data.message || '已处理，请查看警告');
          Modal.info({
            title: '处理结果',
            content: (
              <div>
                <p>{data.message}</p>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  {data.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            ),
          });
        } else {
          message.success(data?.message || '已处理');
        }
        return shipmentApi.get(detailShipment!.shipmentId);
      })
      .then((res) => {
        setDetailShipment(res.data);
        refreshDetailCartons();
        load();
      })
      .catch((e) => {
        message.error(e.response?.data?.message || '处理失败');
        return Promise.reject(e);
      })
      .finally(() => setResolveCargoSyncLoading(false));
  };

  const getShipmentStatus = (s: ShipmentDTO) => String(s?.status ?? '').trim().toUpperCase();
  /** 已签收或已撤销：详情只读 */
  const isShipmentLocked = (s: ShipmentDTO) => ['DELIVERED', 'CANCELLED'].includes(getShipmentStatus(s));

  const getStatusDisplay = (row: ListRow): string => {
    const st = getShipmentStatus(row.s);
    return statusLabel[st] || row.s.status || st;
  };

  const openRemarkModal = (s: ShipmentDTO) => {
    setRemarkModal(s);
    remarkForm.setFieldsValue({ remark: s.remark || '' });
  };

  const openCountryModal = (s: ShipmentDTO) => {
    setCountryModal(s);
    countryForm.setFieldsValue({
      destinationCountryCode: s.destinationCountryCode || undefined,
      destinationCountryName: s.destinationCountryName || s.destinationCountry || undefined,
    });
  };

  const openRequiresCustomsModal = (s: ShipmentDTO) => {
    setRequiresCustomsModal(s);
    requiresCustomsForm.setFieldsValue({ requiresCustoms: s.requiresCustoms !== false });
  };

  const openContainsBatteryModal = (s: ShipmentDTO) => {
    setContainsBatteryModal(s);
    containsBatteryForm.setFieldsValue({ containsBattery: s.containsBattery === true });
  };

  const onSaveRequiresCustoms = () => {
    if (!requiresCustomsModal) return;
    requiresCustomsForm.validateFields().then((values) => {
      setRequiresCustomsSubmitting(true);
      shipmentApi
        .updateRequiresCustoms(requiresCustomsModal.shipmentId, values.requiresCustoms === true)
        .then(() => {
          message.success('是否报关已更新');
          setRequiresCustomsModal(null);
          load();
          if (detailShipment?.shipmentId === requiresCustomsModal.shipmentId) {
            shipmentApi.get(requiresCustomsModal.shipmentId).then((res) => setDetailShipment(res.data)).catch(() => {});
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '更新失败'))
        .finally(() => setRequiresCustomsSubmitting(false));
    });
  };

  const onSaveContainsBattery = () => {
    if (!containsBatteryModal) return;
    containsBatteryForm.validateFields().then((values) => {
      setContainsBatterySubmitting(true);
      shipmentApi
        .updateContainsBattery(containsBatteryModal.shipmentId, values.containsBattery === true)
        .then(() => {
          message.success('是否带电已更新');
          setContainsBatteryModal(null);
          load();
          if (detailShipment?.shipmentId === containsBatteryModal.shipmentId) {
            shipmentApi.get(containsBatteryModal.shipmentId).then((res) => setDetailShipment(res.data)).catch(() => {});
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '更新失败'))
        .finally(() => setContainsBatterySubmitting(false));
    });
  };

  const onUploadCustomsDoc = (file: File) => {
    if (!detailShipment?.shipmentId) return false;
    setCustomsDocUploading(true);
    shipmentApi
      .uploadCustomsDoc(detailShipment.shipmentId, file)
      .then(() => {
        message.success('报关资料已上传');
        refreshDetailCustomsDocs();
        shipmentApi.get(detailShipment.shipmentId).then((res) => setDetailShipment(res.data)).catch(() => {});
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '上传失败'))
      .finally(() => setCustomsDocUploading(false));
    return false;
  };

  const onDownloadCustomsDoc = (doc: ShipmentCustomsDocDTO) => {
    if (!detailShipment?.shipmentId || !doc.id) return;
    shipmentApi
      .downloadCustomsDoc(detailShipment.shipmentId, doc.id)
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName || 'customs-doc';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => message.error('下载失败'));
  };

  const onDeleteCustomsDoc = (doc: ShipmentCustomsDocDTO) => {
    if (!detailShipment?.shipmentId || !doc.id) return;
    Modal.confirm({
      title: '删除报关资料',
      content: `确认删除「${doc.fileName || doc.id}」？`,
      onOk: () =>
        shipmentApi
          .deleteCustomsDoc(detailShipment.shipmentId, doc.id)
          .then(() => {
            message.success('已删除');
            refreshDetailCustomsDocs();
            shipmentApi.get(detailShipment.shipmentId).then((res) => setDetailShipment(res.data)).catch(() => {});
            load();
          })
          .catch((e) => message.error(e.response?.data?.message || '删除失败')),
    });
  };

  const openPieceCountModal = (s: ShipmentDTO) => {
    setPieceCountModal(s);
    pieceCountForm.setFieldsValue({
      declaredPieceCount: s.declaredPieceCount ?? s.cartonCount,
    });
  };

  const onSavePieceCount = () => {
    if (!pieceCountModal) return;
    pieceCountForm.validateFields().then((values) => {
      setPieceCountSubmitting(true);
      shipmentApi
        .updatePieceCount(pieceCountModal.shipmentId, Number(values.declaredPieceCount))
        .then(() => {
          message.success('货物箱数已更新');
          setPieceCountModal(null);
          pieceCountForm.resetFields();
          load();
          if (detailShipment?.shipmentId === pieceCountModal.shipmentId) {
            shipmentApi.get(pieceCountModal.shipmentId).then((res) => setDetailShipment(res.data)).catch(() => {});
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '更新失败'))
        .finally(() => setPieceCountSubmitting(false));
    });
  };

  const onSaveRemark = () => {
    if (!remarkModal) return;
    remarkForm.validateFields().then((values) => {
      setRemarkSubmitting(true);
      const nextRemark = (values.remark || '').trim();
      shipmentApi
        .updateRemark(remarkModal.shipmentId, nextRemark)
        .then(() => {
          message.success('备注已保存');
          setRemarkModal(null);
          remarkForm.resetFields();
          load();
          if (detailShipment?.shipmentId === remarkModal.shipmentId) {
            setDetailShipment({ ...detailShipment, remark: nextRemark || undefined });
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '保存备注失败'))
        .finally(() => setRemarkSubmitting(false));
    });
  };

  const onSaveCountry = () => {
    if (!countryModal) return;
    countryForm.validateFields().then((values) => {
      setCountrySubmitting(true);
      const destinationCountryCode = values.destinationCountryCode?.trim();
      const destinationCountryName = resolveDestinationCountryName(
        destinationCountryCode,
        values.destinationCountryName
      );
      shipmentApi
        .updateDestinationCountry(countryModal.shipmentId, {
          destinationCountryCode,
          destinationCountryName,
        })
        .then(() => {
          message.success('目的国家已更新');
          setCountryModal(null);
          countryForm.resetFields();
          load();
          if (detailShipment?.shipmentId === countryModal.shipmentId) {
            shipmentApi.get(countryModal.shipmentId).then((res) => setDetailShipment(res.data)).catch(() => {});
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '更新目的国家失败'))
        .finally(() => setCountrySubmitting(false));
    });
  };

  /** 打开修改地址弹窗：加载该客户地址簿（仅预申报可改，后端再校验） */
  const openAddressModal = (s: ShipmentDTO) => {
    setAddressModal(s);
    setAddrEditDetail(null);
    addressForm.resetFields();
    if (!s.clientCode) {
      setAddrEditOptions([]);
      return;
    }
    setAddrEditLoading(true);
    customerApi
      .listAddressSummaries(s.clientCode)
      .then((res) => setAddrEditOptions(res.data || []))
      .catch(() => {
        setAddrEditOptions([]);
        message.error('加载客户地址失败');
      })
      .finally(() => setAddrEditLoading(false));
  };

  const onAddressSelect = (addressId: string) => {
    setAddrEditDetail(null);
    if (!addressId) return;
    customerApi
      .getAddressDetail(addressId)
      .then((res) => setAddrEditDetail(res.data))
      .catch(() => message.error('加载地址详情失败'));
  };

  const onSaveAddress = () => {
    if (!addressModal) return;
    addressForm.validateFields().then((values) => {
      setAddressSubmitting(true);
      shipmentApi
        .updateAddress(addressModal.shipmentId, values.customerAddressId)
        .then(() => {
          message.success('地址已更新');
          setAddressModal(null);
          addressForm.resetFields();
          load();
          if (detailShipment?.shipmentId === addressModal.shipmentId) {
            shipmentApi.get(addressModal.shipmentId).then((res) => setDetailShipment(res.data)).catch(() => {});
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '更新地址失败'))
        .finally(() => setAddressSubmitting(false));
    });
  };

  const openChannelShipmentModal = (s: ShipmentDTO) => {
    setChannelShipmentModal(s);
    channelShipmentForm.setFieldsValue({ channelShipmentId: s.channelShipmentId || '' });
  };

  const onSaveChannelShipmentId = () => {
    if (!channelShipmentModal) return;
    channelShipmentForm.validateFields().then((values) => {
      setChannelShipmentSubmitting(true);
      const nextChannelShipmentId = (values.channelShipmentId || '').trim();
      shipmentApi
        .setChannelShipmentId(channelShipmentModal.shipmentId, nextChannelShipmentId)
        .then(() => {
          message.success('转单号已保存');
          setChannelShipmentModal(null);
          channelShipmentForm.resetFields();
          load();
          if (detailShipment?.shipmentId === channelShipmentModal.shipmentId) {
            setDetailShipment({
              ...detailShipment,
              channelShipmentId: nextChannelShipmentId || undefined,
            });
          }
        })
        .catch((e) => message.error(e.response?.data?.message || '保存转单号失败'))
        .finally(() => setChannelShipmentSubmitting(false));
    });
  };

  const listBaseColumns: ResizableColumnType<ListRow>[] = useMemo(
    () => [
      { key: 'status', title: '状态', render: (_, r) => {
        const active = r.s.problemActive === true;
        const ps = (r.s.problemStatus || '').trim().toUpperCase();
        const tip =
          ps === 'IN_PROGRESS'
            ? `问题件 · ${problemStatusLabel.IN_PROGRESS}`
            : ps === 'OPEN' || !ps
              ? `问题件 · ${problemStatusLabel.OPEN}`
              : '问题件';
        return (
          <Space size={4}>
            {active && (
              <Tooltip title={tip}>
                <WarningFilled style={{ color: '#cf1322', fontSize: 14 }} aria-label="问题件" />
              </Tooltip>
            )}
            <span>{getStatusDisplay(r)}</span>
          </Space>
        );
      } },
      {
        key: 'clientCode',
        title: '客户',
        render: (_, r) => r.s.clientCode,
      },
      {
        key: 'createdAt',
        title: '创建日期',
        render: (_, r) => (r.s.createdAt ? dayjs(r.s.createdAt).format('YYYY-MM-DD') : '-'),
      },
      {
        key: 'shipmentId',
        title: '运单号',
        render: (_, r) => highlightText(r.s.shipmentId, appliedShipmentIdKeyword),
      },
      {
        key: 'channelShipmentId',
        title: '转单号',
        render: (_, r) => {
          const no = r.s.channelShipmentId?.trim();
          if (!no) return '-';
          return (
            <div>
              <div>{no}</div>
              <CarrierTrackingLink shipment={r.s} />
            </div>
          );
        },
      },
      {
        key: 'vessel',
        title: '船名航次',
        render: (_, r) => {
          const name = r.s.vesselName?.trim();
          if (!name) return '-';
          const text = r.s.vesselVoyage?.trim() ? `${name} ${r.s.vesselVoyage.trim()}` : name;
          // 无转单号时（如 K26105232）查船链接放在本列
          if (!r.s.channelShipmentId?.trim()) {
            return (
              <div>
                <div>{text}</div>
                <CarrierTrackingLink shipment={r.s} />
              </div>
            );
          }
          return text;
        },
      },
      {
        key: 'remark',
        title: '备注',
        wrapMaxWidth: 240,
        render: (_, r) => r.s.remark?.trim() || '-',
      },
      {
        key: 'destinationCountry',
        title: '国家',
        render: (_, r) => r.s.destinationCountry || '-',
      },
      {
        key: 'requiresCustoms',
        title: '报关',
        render: (_, r) => {
          const yes = r.s.requiresCustoms !== false;
          return yes ? `是(${r.s.customsDocCount ?? 0})` : '否';
        },
      },
      {
        key: 'containsBattery',
        title: '带电',
        render: (_, r) => (r.s.containsBattery === true ? '是' : '否'),
      },
      {
        key: 'cartonCount',
        title: '箱数',
        render: (_, r) => r.s.cartonCount ?? 0,
      },
      {
        key: 'confirmedWeight',
        title: '代理计费重',
        render: (_, r) => (r.s.confirmedWeight != null ? r.s.confirmedWeight : '-'),
      },
      {
        key: 'agentIdentifier',
        title: '代理',
        render: (_, r) => r.s.agentIdentifier || '-',
      },
      {
        key: 'channel',
        title: '渠道',
        wrapMaxWidth: 240,
        wrapMaxWidthOf: 'remark',
        render: (_, r) => channelDisplay(r.s),
      },
      {
        key: 'agentInboundNo',
        title: '代理入库单号',
        render: (_, r) => r.s.agentInboundNo || '-',
      },
    ],
    [appliedShipmentIdKeyword]
  );

  const agentFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    for (const a of agents) {
      const name = a.basicInfo?.name?.trim();
      const identifier = a.basicInfo?.identifier?.trim();
      const value = name || identifier;
      if (!value || seen.has(value)) continue;
      seen.add(value);
      opts.push({
        value,
        label: name && identifier && name !== identifier ? `${name} (${identifier})` : value,
      });
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'));
  }, [agents]);

  const listColumns = useResizableColumns(listBaseColumns, 'shipmentList.v5', { autoFit: true });

  return (
    <>
      <Card
        title="运单列表"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            创建预申报
          </Button>
        }
      >
        <ShipmentStatusCards
          counts={statusCounts}
          totalAll={statusTotal}
          activeStatus={status}
          onSelect={onStatusCardSelect}
        />
        <FilterDrawer dense>
        <Input
          placeholder="模糊搜索运单号"
          allowClear
          style={{ width: 200, marginRight: 8 }}
          value={filterShipmentIdInput}
          onChange={(e) => setFilterShipmentIdInput(e.target.value)}
          onPressEnter={applyFilters}
        />
        <Input
          placeholder="客户编号"
          allowClear
          style={{ width: 160, marginRight: 8 }}
          value={filterClientCodeInput}
          onChange={(e) => setFilterClientCodeInput(e.target.value)}
          onPressEnter={applyFilters}
        />
        <Select
          style={{ width: 130, marginRight: 8 }}
          value={filterProblemInput}
          onChange={(v) => setFilterProblemInput(v)}
          options={[
            { value: 'ALL', label: '全部' },
            { value: 'ACTIVE', label: '仅问题件' },
            { value: 'INACTIVE', label: '非问题件' },
          ]}
        />
        <Select
          placeholder="代理名称"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 220, marginRight: 8 }}
          options={agentFilterOptions}
          value={filterAgentNameInput}
          onChange={(v) => setFilterAgentNameInput(v)}
        />
        <Button type="primary" onClick={applyFilters} style={{ marginRight: 8 }}>
          查询
        </Button>
        <Button onClick={resetFilters}>重置</Button>
        </FilterDrawer>
      </Card>
      <Card>
        <AdaptiveTable
          loading={loading}
          dataSource={rows}
          rowKey="key"
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
          components={{ header: { cell: ResizableTitle } }}
          onRow={(record) => ({
            onClick: () => openDetailDrawer(record.s),
            style: {
              cursor: 'pointer',
              background: record.s.problemActive === true ? '#fff1f0' : undefined,
            },
          })}
          columns={listColumns}
          pagination={serverTablePagination({
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          })}
        />
      </Card>

      <Modal
        title="创建预申报"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
          setManualShipmentId(false);
          setAddressDetail(null);
          setAddresses([]);
        }}
        footer={null}
        width={560}
      >
        <Form form={createForm} layout="vertical" onFinish={onFinishCreate} initialValues={{ manualShipmentId: false, requiresCustoms: true, containsBattery: false }}>
          <Form.Item label="手动输入运单号">
            <Switch
              checked={manualShipmentId}
              onChange={(v) => {
                setManualShipmentId(v);
                if (!v) createForm.setFieldValue('shipmentId', undefined);
              }}
            />
            <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>默认自动生成（K+年月+4位数字）；开启后可填写代理系统已有运单号</span>
          </Form.Item>
          {manualShipmentId && (
            <Form.Item
              name="shipmentId"
              label="运单号"
              rules={[{ required: true, message: '请输入运单号' }]}
            >
              <Input placeholder="例如代理系统中的客户单号" />
            </Form.Item>
          )}
          <Form.Item name="clientCode" label="客户编号" rules={[{ required: true, message: '请选择客户' }]}>
            <Select
              placeholder="从客户列表选择"
              showSearch
              optionFilterProp="label"
              onChange={onCustomerChange}
              options={customers.map((c) => ({ label: `${c.customerCode} - ${c.name || ''}`, value: c.customerCode }))}
            />
          </Form.Item>
          <Form.Item label="客户地址" required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="clientAddr" noStyle rules={[{ required: true, message: '请选择地址' }]}>
                <Select
                  style={{ width: '100%' }}
                  placeholder={addresses.length ? '选择地址' : '请先选择客户'}
                  options={addresses.map((a) => ({
                    label: [a.label, a.remark].filter(Boolean).join(' | ') || a.id,
                    value: a.id,
                  }))}
                  disabled={!addresses.length}
                  onChange={() => setAddressDetail(null)}
                />
              </Form.Item>
              <Button
                onClick={() => loadAddressDetail(createForm.getFieldValue('clientAddr'))}
                loading={addressDetailLoading}
              >
                查看详情
              </Button>
            </Space.Compact>
          </Form.Item>
          {addressDetail && (
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fafafa', borderRadius: 4, fontSize: 13, color: '#555' }}>
              <div>联系人：{addressDetail.contactName || '-'}</div>
              <div>电话：{addressDetail.phone || '-'}</div>
              <div>地址：{addressDetail.address || '-'}</div>
              <div>邮编：{addressDetail.postalCode || '-'}</div>
            </div>
          )}
          <Form.Item
            name="destinationCountryCode"
            label="目的国家"
            rules={[{ required: true, message: '请选择目的国家' }]}
          >
            <Select
              placeholder="请选择目的国家"
              showSearch
              optionFilterProp="label"
              onChange={(code: string) => {
                createForm.setFieldValue('destinationCountryName', resolveDestinationCountryName(code));
              }}
              options={COUNTRY_SELECT_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="destinationCountryName" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="transportMode" label="运输方式" rules={[{ required: true, message: '请选择运输方式' }]}>
            <Select
              placeholder="请选择运输方式"
              options={transportModes.map((t) => ({
                label: t.displayName || t.mode,
                value: t.mode,
              }))}
            />
          </Form.Item>
          <Form.Item name="agentId" label="代理" extra="可选，交仓前可再补全">
            <Select
              allowClear
              placeholder="可选"
              showSearch
              optionFilterProp="label"
              onChange={onCreateAgentChange}
              options={agents.map((a) => ({
                label: agentLabel(a),
                value: a.id,
              }))}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.agentId !== cur.agentId}>
            {() => {
              const agentId = createForm.getFieldValue('agentId') as string | undefined;
              const filtered = channelsForAgent(channels, agents, agentId);
              return (
                <Form.Item name="channelId" label="渠道" extra="可选，交仓前可再补全">
                  <Select
                    allowClear
                    placeholder={agentId ? '可选' : '可先选代理，或稍后补全'}
                    disabled={!agentId}
                    options={filtered.map((c) => ({
                      label: `${c.channelCode}${c.channelName ? ` (${c.channelName})` : ''}`,
                      value: c.id,
                    }))}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            name="declaredPieceCount"
            label="货物箱数"
            rules={[
              { required: true, message: '请输入货物箱数' },
              {
                validator: async (_, v) => {
                  const n = Number(v);
                  if (!Number.isInteger(n) || n < 0) throw new Error('须为 0–999 的整数');
                  if (n > 999) throw new Error('不能超过 999');
                },
              },
            ]}
          >
            <InputNumber min={0} max={999} precision={0} style={{ width: '100%' }} placeholder="可先填 0，后续再改；创建时不建箱" />
          </Form.Item>
          <Form.Item
            name="requiresCustoms"
            label="是否报关"
            valuePropName="checked"
            rules={[{ required: true, message: '请选择是否报关' }]}
            extra="建议交仓前上传报关资料；未上传时提交会二次确认"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item
            name="containsBattery"
            label="是否带电"
            valuePropName="checked"
            rules={[{ required: true, message: '请选择是否带电' }]}
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item name="cargoDescJson" label="货物描述">
            <Input.TextArea rows={3} placeholder="请输入货物描述（文本）" />
          </Form.Item>
          <Form.Item
            name="remark"
            label="备注"
            rules={[{ max: REMARK_MAX_LENGTH, message: `备注最长 ${REMARK_MAX_LENGTH} 字` }]}
          >
            <Input.TextArea rows={2} placeholder="可选" maxLength={REMARK_MAX_LENGTH} showCount />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              确定创建
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setCreateModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="绑定货物批次"
        open={bindModalOpen}
        onCancel={() => {
          setBindModalOpen(false);
          setBindingShipment(null);
        }}
        onOk={onSaveBatchBinding}
        okText="保存绑定"
        confirmLoading={bindBatchLoading}
        width={720}
        styles={{ body: { overflowX: 'hidden' } }}
      >
        {bindingShipment && (
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>
              <span style={{ marginRight: 8 }}>当前运单：</span>
              <strong>{bindingShipment.shipmentId}</strong>
              <span style={{ marginLeft: 8, color: '#666' }}>（客户 {bindingShipment.clientCode}）</span>
            </span>
            <Button onClick={() => setBatchFormShipment(bindingShipment)}>新增批次</Button>
          </div>
        )}
        <AdaptiveTable<CartonBatchDTO>
          loading={batchesForBindLoading}
          rowSelection={{
            selectedRowKeys: selectedBatchIds,
            onChange: (keys) => setSelectedBatchIds(keys as string[]),
          }}
          dataSource={batchesForBind}
          rowKey="id"
          size="small"
          scroll={{ x: 980 }}
          locale={{ emptyText: '该客户暂无可绑定的批次，可点「新增批次」' }}
          columns={[
            { title: '批次标识', dataIndex: 'batchId', width: 150, ellipsis: true },
            { title: '件数', dataIndex: 'pieceCount', width: 64, render: (v?: number) => v ?? '-' },
            { title: '已生成', dataIndex: 'materializedCount', width: 72, render: (v?: number) => v ?? 0 },
            { title: '货物品名', dataIndex: 'cargoName', width: 120, ellipsis: true, render: (v?: string) => v || '-' },
            { title: '客户', dataIndex: 'belongClientId', width: 100, ellipsis: true, render: (v: string) => customers.find((c) => c.id === v || c.customerCode === v)?.name || v || '-' },
            {
              title: '绑定',
              dataIndex: 'shipmentId',
              width: 80,
              render: (v?: string) => (v ? <Tag color="blue">本运单</Tag> : <Tag>未绑定</Tag>),
            },
            { title: '备注', dataIndex: 'batchRemark', width: 100, ellipsis: true, render: (v?: string) => v || '-' },
            { title: '创建时间', dataIndex: 'createTimestamp', width: 150, render: (t: number) => (t ? new Date(t).toLocaleString() : '-') },
          ]}
          pagination={clientTablePagination({ defaultPageSize: 8 })}
        />
        <div style={{ marginTop: 8, color: '#666' }}>
          已选 {selectedBatchIds.length} 批，合计{' '}
          <strong>
            {batchesForBind
              .filter((b) => b.id && selectedBatchIds.includes(b.id))
              .reduce((sum, b) => sum + (b.pieceCount ?? 0), 0)}
          </strong>{' '}
          件（运单件数 = 绑定批次件数之和）
        </div>
      </Modal>

      <CartonBatchFormModal
        open={!!batchFormShipment}
        title={batchFormShipment ? `新增批次并绑定运单 ${batchFormShipment.shipmentId}` : undefined}
        shipmentId={batchFormShipment?.shipmentId}
        fixedClientId={batchFormShipment ? customerIdOfShipment(batchFormShipment) : undefined}
        defaultBatchId={batchFormShipment ? nextBatchIdFor(batchFormShipment) : undefined}
        onClose={() => setBatchFormShipment(null)}
        onCreated={() => batchFormShipment && onBatchCreatedForShipment(batchFormShipment.shipmentId)}
      />

      <AgentCargoBatchMatchModal
        open={!!matchModal}
        shipmentId={matchModal?.shipmentId}
        onClose={() => setMatchModal(null)}
        onDone={() => {
          const sid = matchModal?.shipmentId;
          setMatchModal(null);
          if (sid) refreshShipmentAfterBatchChange(sid);
        }}
      />

      <WeightConfirmModal
        shipment={weightModal}
        onClose={() => setWeightModal(null)}
        onConfirmed={onWeightConfirmed}
      />

      <Modal
        title="交代理仓"
        open={!!submitDepotModal}
        onOk={onSubmitDepotOk}
        onCancel={() => {
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          setCheckAgentMiss(null);
          setManualAgentInboundNo('');
          submitDepotForm.resetFields();
        }}
        confirmLoading={submitDepotSubmitting}
        width={560}
        destroyOnClose
      >
        {submitDepotModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{submitDepotModal.shipmentId}</strong> 将提交至代理仓，请选择目标仓库并填写交仓信息。
            </p>
            {checkAgentMiss && !checkAgentMiss.exists && (
              <div style={{ marginBottom: 12, padding: 10, background: '#fffbe6', borderRadius: 6, fontSize: 12, color: '#ad6800' }}>
                {checkAgentMiss.needManualInboundNo || checkAgentMiss.agentIdentifier === '56yorky'
                  ? checkAgentMiss.message ||
                    '已下单但是仓库还未出数据，需手动填入入仓单号或稍后再试'
                  : `代理侧未查到已有运单${checkAgentMiss.message ? `：${checkAgentMiss.message}` : '。'}`}
                {isIntelinkQ5Identifier(checkAgentMiss.agentIdentifier || submitDepotContext?.agent?.basicInfo?.identifier) && (
                  <> 若确认哲盟已有该单号，可勾选下方「强制绑定」直接同步，无需新建。</>
                )}
                {(checkAgentMiss.needManualInboundNo ||
                  (checkAgentMiss.agentIdentifier || '').toLowerCase() === '56yorky') && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ marginBottom: 6, color: 'rgba(0,0,0,0.65)' }}>代理入仓单号</div>
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        placeholder="例如 11089610"
                        value={manualAgentInboundNo}
                        onChange={(e) => setManualAgentInboundNo(e.target.value)}
                        allowClear
                      />
                      <Button
                        type="primary"
                        loading={manualInboundVerifying}
                        onClick={() => {
                          const inbound = manualAgentInboundNo.trim();
                          if (!inbound) {
                            message.warning('请填写代理入仓单号');
                            return;
                          }
                          if (!submitDepotModal) return;
                          setManualInboundVerifying(true);
                          shipmentApi
                            .checkAgentOrder(submitDepotModal.shipmentId, { agentInboundNo: inbound })
                            .then((res) => {
                              const check = res.data;
                              if (check?.exists) {
                                const cargo = check.cargoItems || [];
                                setSyncCargoSelectedKeys(
                                  cargo.map((c: AgentCargoItemDTO, i: number) => c.sourceIndex ?? i)
                                );
                                setSyncConfirmModal({ shipment: submitDepotModal, check });
                                setSubmitDepotModal(null);
                                setSubmitDepotContext(null);
                                setCheckAgentMiss(null);
                                setManualAgentInboundNo('');
                                submitDepotForm.resetFields();
                                message.success('入仓单号校验通过，请确认后同步');
                              } else {
                                setCheckAgentMiss(check || null);
                                message.error(check?.message || '入仓单号校验失败');
                              }
                            })
                            .catch((e) => message.error(e.response?.data?.message || '校验失败'))
                            .finally(() => setManualInboundVerifying(false));
                        }}
                      >
                        校验并同步
                      </Button>
                    </Space.Compact>
                    <div style={{ marginTop: 4, color: 'rgba(0,0,0,0.45)' }}>
                      从 56yorky 后台复制入库号；将校验客户单号与本运单一致
                    </div>
                  </div>
                )}
              </div>
            )}
            {submitDepotContextLoading ? (
              <Spin spinning />
            ) : submitDepotContext ? (
              <>
                {submitDepotContext.agent && (
                  <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ marginBottom: 4 }}>
                      <strong>代理：</strong>
                      {submitDepotContext.agent.basicInfo?.name || submitDepotContext.agent.basicInfo?.identifier || '-'}
                    </div>
                    {submitDepotContext.agent.contact?.contactPerson && (
                      <div style={{ color: '#666', fontSize: 12 }}>
                        对接人：{submitDepotContext.agent.contact.contactPerson.name} {submitDepotContext.agent.contact.contactPerson.phone}
                      </div>
                    )}
                    {needsServiceChannelSelect(submitDepotContext.agent.basicInfo?.identifier) &&
                      missingQ5ConsigneeFields(submitDepotModal.clientAddr, submitDepotModal.clientCode).length > 0 && (
                        <div style={{ marginTop: 8, color: '#d46b08', fontSize: 12 }}>
                          {agentDisplayName(submitDepotContext.agent.basicInfo?.identifier)} 需要完整收件信息，当前缺少：
                          {missingQ5ConsigneeFields(submitDepotModal.clientAddr, submitDepotModal.clientCode).join('、')}
                          。请先完善客户地址簿中的联系人电话后重建预申报，或更新本运单 clientAddr。
                        </div>
                      )}
                  </div>
                )}
                <Form form={submitDepotForm} layout="vertical">
                  {isIntelinkQ5Identifier(submitDepotContext.agent?.basicInfo?.identifier) &&
                    checkAgentMiss &&
                    !checkAgentMiss.exists && (
                      <Form.Item name="forceBind" valuePropName="checked">
                        <Checkbox>确认哲盟已有该运单号，强制绑定同步（不创建订单）</Checkbox>
                      </Form.Item>
                    )}
                  <Form.Item noStyle shouldUpdate={(prev, cur) => prev.forceBind !== cur.forceBind}>
                    {() => {
                      const force = !!submitDepotForm.getFieldValue('forceBind');
                      return (
                        <>
                          <Form.Item
                            name="depotId"
                            label="代理仓库"
                            rules={force ? [] : [{ required: true, message: '请选择目标仓库' }]}
                          >
                            <Select
                              placeholder="选择仓库"
                              disabled={force}
                              options={(submitDepotContext.depots || []).map((d) => ({
                                label: d.depotName ? `${d.depotName}${d.depotId ? ` (${d.depotId})` : ''}` : String(d.depotId ?? d.id ?? ''),
                                value: String(d.depotId ?? d.id ?? ''),
                              }))}
                              allowClear={false}
                            />
                          </Form.Item>
                          {needsServiceChannelSelect(submitDepotContext.agent?.basicInfo?.identifier) && !force && (
                            <>
                              <Form.Item
                                name="hubInCode"
                                label="服务渠道（hubInCode）"
                                rules={
                                  (submitDepotContext.serviceChannels || []).some((c) => c.serviceCode)
                                    ? [{ required: true, message: `请选择${agentDisplayName(submitDepotContext.agent?.basicInfo?.identifier)}服务渠道` }]
                                    : []
                                }
                                extra="对应代理服务渠道编码；须先在代理管理中同步服务渠道"
                              >
                                <Select
                                  placeholder="选择服务渠道"
                                  options={(submitDepotContext.serviceChannels || [])
                                    .filter((c) => c.serviceCode)
                                    .map((c) => ({
                                      label: c.serviceName
                                        ? `${c.serviceName} (${c.serviceCode})`
                                        : String(c.serviceCode),
                                      value: String(c.serviceCode),
                                    }))}
                                  showSearch
                                  optionFilterProp="label"
                                  allowClear={false}
                                />
                              </Form.Item>
                              <Form.Item
                                name="weig"
                                label="交仓重量 weig（kg）"
                                rules={
                                  submitDepotContext.weigRequiredForCreate
                                    ? [{ required: true, message: `${agentDisplayName(submitDepotContext.agent?.basicInfo?.identifier)} 新建订单需要重量` }]
                                    : []
                                }
                                extra={
                                  submitDepotContext.suggestedWeig != null
                                    ? `已根据运单/货物汇总建议 ${submitDepotContext.suggestedWeig} kg，可修改`
                                    : '运单与货物均无重量时必填'
                                }
                              >
                                <InputNumber min={0.001} step={0.1} style={{ width: '100%' }} placeholder="kg" />
                              </Form.Item>
                            </>
                          )}
                        </>
                      );
                    }}
                  </Form.Item>
                  <Form.Item name="huolalaOrderNo" label="货拉拉单号">
                    <Input placeholder="选填" />
                  </Form.Item>
                  <Form.Item name="photoUrls" label="照片（多张 URL 空格分隔）">
                    <Input.TextArea rows={2} placeholder="选填，多张照片 URL 用空格分隔" />
                  </Form.Item>
                </Form>
              </>
            ) : null}
          </>
        )}
      </Modal>

      <Modal
        title="同步代理已有运单"
        open={!!syncConfirmModal}
        onOk={onSyncExistingOk}
        onCancel={() => {
          setSyncConfirmModal(null);
          setSyncCargoSelectedKeys([]);
        }}
        okText={
          syncCargoSelectedKeys.length > 0
            ? `同步运单+货物（${syncCargoSelectedKeys.length} 箱）`
            : '同步运单'
        }
        confirmLoading={syncConfirmSubmitting}
        width={860}
        destroyOnClose
      >
        {syncConfirmModal && (
          <>
            <p style={{ marginBottom: 16 }}>
              代理系统中已存在运单 <strong>{syncConfirmModal.shipment.shipmentId}</strong>，请对比确认后同步绑定（不会在代理侧新建订单）。
            </p>
            {(() => {
              const k = syncConfirmModal.check.kfy;
              const eff = k?.effectivePieceCount ?? k?.cartonCount ?? 0;
              const agentN = syncConfirmModal.check.cargoItems?.length ?? 0;
              if (agentN > 0 && eff !== agentN) {
                return (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`货物箱数冲突：凯飞越 ${eff}，代理 ${agentN}。确认同步后将按实际绑定箱数回写并记入操作日志。`}
                  />
                );
              }
              return null;
            })()}
            <AdaptiveTable
              size="small"
              pagination={false}
              rowKey="field"
              dataSource={[
                {
                  field: '运单号/客户单号',
                  kfy: syncConfirmModal.check.kfy?.shipmentId || '-',
                  agent: syncConfirmModal.check.agent?.userNo || '-',
                },
                {
                  field: '代理入库单号',
                  kfy: '-',
                  agent: syncConfirmModal.check.agent?.inputId || syncConfirmModal.check.agentInboundNo || '-',
                },
                {
                  field: '客户',
                  kfy: syncConfirmModal.check.kfy?.clientCode || '-',
                  agent: '-',
                },
                {
                  field: '渠道 / 服务渠道',
                  kfy: channelDisplay(syncConfirmModal.shipment) !== '-'
                    ? channelDisplay(syncConfirmModal.shipment)
                    : (syncConfirmModal.check.kfy?.routeName || '-'),
                  agent: syncConfirmModal.check.agent?.serviceName || '-',
                },
                {
                  field: '货物箱数',
                  kfy: String(
                    syncConfirmModal.check.kfy?.effectivePieceCount
                      ?? syncConfirmModal.check.kfy?.cartonCount
                      ?? 0
                  ),
                  agent: String(syncConfirmModal.check.cargoItems?.length ?? 0),
                  conflict: (() => {
                    const k = syncConfirmModal.check.kfy;
                    const eff = k?.effectivePieceCount ?? k?.cartonCount ?? 0;
                    const agentN = syncConfirmModal.check.cargoItems?.length ?? 0;
                    return agentN > 0 && eff !== agentN;
                  })(),
                },
                {
                  field: '状态 / 最新轨迹',
                  kfy: statusLabel[syncConfirmModal.check.kfy?.status || ''] || syncConfirmModal.check.kfy?.status || '-',
                  agent: syncConfirmModal.check.agent?.trackSummary || '-',
                },
              ]}
              columns={[
                { title: '字段', dataIndex: 'field', width: 140 },
                {
                  title: '凯飞越',
                  dataIndex: 'kfy',
                  render: (v: string, r: { conflict?: boolean }) =>
                    r.conflict ? <span style={{ color: '#cf1322', fontWeight: 600 }}>{v}</span> : v,
                },
                {
                  title: '代理系统',
                  dataIndex: 'agent',
                  render: (v: string, r: { conflict?: boolean }) =>
                    r.conflict ? <span style={{ color: '#cf1322', fontWeight: 600 }}>{v}</span> : v,
                },
              ]}
            />
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong>代理货物清单</strong>
                <Space>
                  <Button
                    size="small"
                    onClick={() => {
                      const cargo = syncConfirmModal.check.cargoItems || [];
                      setSyncCargoSelectedKeys(cargo.map((c, i) => c.sourceIndex ?? i));
                    }}
                  >
                    全选
                  </Button>
                  <Button size="small" onClick={() => setSyncCargoSelectedKeys([])}>
                    取消
                  </Button>
                </Space>
              </div>
              {syncConfirmModal.check.cargoFetchMessage && (
                <div style={{ marginBottom: 8, color: '#ad6800', fontSize: 12 }}>
                  {syncConfirmModal.check.cargoFetchMessage}
                </div>
              )}
              <AdaptiveTable
                size="small"
                rowKey={(r) => String(r.sourceIndex ?? r.cartonId)}
                pagination={false}
                scroll={{ y: 240 }}
                rowSelection={{
                  selectedRowKeys: syncCargoSelectedKeys,
                  onChange: (keys) => setSyncCargoSelectedKeys(keys),
                }}
                dataSource={syncConfirmModal.check.cargoItems || []}
                locale={{ emptyText: '暂无代理货物测量数据' }}
                columns={[
                  { title: '箱号', dataIndex: 'cartonId', width: 160, ellipsis: true },
                  {
                    title: '长×宽×高(cm)',
                    width: 140,
                    render: (_: unknown, r: AgentCargoItemDTO) =>
                      [r.lengthCm, r.widthCm, r.heightCm].every((v) => v != null)
                        ? `${r.lengthCm}×${r.widthCm}×${r.heightCm}`
                        : '-',
                  },
                  {
                    title: '毛重(kg)',
                    dataIndex: 'measuredWeightKg',
                    width: 90,
                    render: (v: number) => (v != null ? v : '-'),
                  },
                  {
                    title: '计费重(kg)',
                    dataIndex: 'billingWeightKg',
                    width: 100,
                    render: (v: number) => (v != null ? v : '-'),
                  },
                  {
                    title: '体积',
                    dataIndex: 'volumeCbm',
                    width: 80,
                    render: (v: number) => (v != null ? v : '-'),
                  },
                  { title: '库位', dataIndex: 'locationCode', width: 100, ellipsis: true, render: (v: string) => v || '-' },
                  ...((syncConfirmModal.check.boundBatches?.length ?? 0) > 0
                    ? [{
                      title: '归属批次',
                      width: 200,
                      render: (_: unknown, r: AgentCargoItemDTO, i: number) => {
                        const k = r.sourceIndex ?? i;
                        if (r.existingCartonId) return <Tag>已生成</Tag>;
                        return (
                          <Select
                            size="small"
                            style={{ width: '100%' }}
                            value={syncBatchAssign[k]}
                            placeholder="选择批次"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(v: string) => setSyncBatchAssign((prev) => ({ ...prev, [k]: v }))}
                            options={(syncConfirmModal.check.boundBatches || []).map((b) => ({
                              value: b.batchId || '',
                              label: `${b.batchId}（${b.pieceCount ?? 0} 件）`,
                            }))}
                          />
                        );
                      },
                    }]
                    : []),
                ]}
              />
              {(syncConfirmModal.check.boundBatches?.length ?? 0) > 0 && (
                <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                  运单已绑定 {syncConfirmModal.check.boundBatches!.length} 个货物批次：勾选的代理箱将按「归属批次」生成单箱，并按实际箱数更新批次件数。
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      <Modal
        title="更新运输方式"
        open={!!updateRouteModal}
        onOk={onUpdateRouteOk}
        onCancel={() => {
          setUpdateRouteModal(null);
          updateRouteForm.resetFields();
        }}
        confirmLoading={updateRouteSubmitting}
        destroyOnClose
      >
        {updateRouteModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{updateRouteModal.shipmentId}</strong>
            </p>
            <Form form={updateRouteForm} layout="vertical">
              <Form.Item name="transportMode" label="运输方式" rules={[{ required: true, message: '请选择运输方式' }]}>
                <Select
                  options={transportModes.map((t) => ({
                    label: t.displayName || t.mode,
                    value: t.mode,
                  }))}
                />
              </Form.Item>
              <Form.Item name="agentId" label="代理" rules={[{ required: true, message: '请选择代理' }]}>
                <Select
                  placeholder="请选择代理"
                  showSearch
                  optionFilterProp="label"
                  onChange={onUpdateAgentChange}
                  options={agents.map((a) => ({
                    label: agentLabel(a),
                    value: a.id,
                  }))}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.agentId !== cur.agentId}>
                {() => {
                  const agentId = updateRouteForm.getFieldValue('agentId') as string | undefined;
                  const filtered = channelsForAgent(channels, agents, agentId);
                  return (
                    <Form.Item name="channelId" label="渠道" rules={[{ required: true, message: '请选择渠道' }]}>
                      <Select
                        placeholder={agentId ? '请选择渠道' : '请先选择代理'}
                        disabled={!agentId}
                        options={filtered.map((c) => ({
                          label: `${c.channelCode}${c.channelName ? ` (${c.channelName})` : ''}`,
                          value: c.id,
                        }))}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="编辑转单号"
        open={!!channelShipmentModal}
        onOk={onSaveChannelShipmentId}
        onCancel={() => {
          setChannelShipmentModal(null);
          channelShipmentForm.resetFields();
        }}
        confirmLoading={channelShipmentSubmitting}
        destroyOnClose
      >
        {channelShipmentModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{channelShipmentModal.shipmentId}</strong>
            </p>
            <Form form={channelShipmentForm} layout="vertical">
              <Form.Item
                name="channelShipmentId"
                label="转单号"
                rules={[{ max: CHANNEL_SHIPMENT_ID_MAX_LENGTH, message: `转单号最长 ${CHANNEL_SHIPMENT_ID_MAX_LENGTH} 字` }]}
              >
                <Input placeholder="UPS/DHL 等 label 单号，可清空" maxLength={CHANNEL_SHIPMENT_ID_MAX_LENGTH} showCount />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="编辑备注"
        open={!!remarkModal}
        onOk={onSaveRemark}
        onCancel={() => {
          setRemarkModal(null);
          remarkForm.resetFields();
        }}
        confirmLoading={remarkSubmitting}
        destroyOnClose
      >
        {remarkModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{remarkModal.shipmentId}</strong>
            </p>
            <Form form={remarkForm} layout="vertical">
              <Form.Item
                name="remark"
                label="备注"
                rules={[{ max: REMARK_MAX_LENGTH, message: `备注最长 ${REMARK_MAX_LENGTH} 字` }]}
              >
                <Input.TextArea rows={3} placeholder="可清空" maxLength={REMARK_MAX_LENGTH} showCount />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="编辑目的国家"
        open={!!countryModal}
        onOk={onSaveCountry}
        onCancel={() => {
          setCountryModal(null);
          countryForm.resetFields();
        }}
        confirmLoading={countrySubmitting}
        destroyOnClose
      >
        {countryModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{countryModal.shipmentId}</strong>
            </p>
            <Form form={countryForm} layout="vertical">
              <Form.Item
                name="destinationCountryCode"
                label="目的国家"
                rules={[{ required: true, message: '请选择目的国家' }]}
              >
                <Select
                  placeholder="请选择目的国家"
                  showSearch
                  optionFilterProp="label"
                  onChange={(code: string) => {
                    countryForm.setFieldValue('destinationCountryName', resolveDestinationCountryName(code));
                  }}
                  options={COUNTRY_SELECT_OPTIONS}
                />
              </Form.Item>
              <Form.Item name="destinationCountryName" hidden>
                <Input />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="修改地址"
        open={!!addressModal}
        onOk={onSaveAddress}
        onCancel={() => {
          setAddressModal(null);
          addressForm.resetFields();
          setAddrEditDetail(null);
        }}
        confirmLoading={addressSubmitting}
        destroyOnClose
      >
        {addressModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{addressModal.shipmentId}</strong>
              <span style={{ color: '#888', marginLeft: 8 }}>仅可选择该客户（{addressModal.clientCode}）地址簿中的地址</span>
            </p>
            <Form form={addressForm} layout="vertical">
              <Form.Item
                name="customerAddressId"
                label="客户地址"
                rules={[{ required: true, message: '请选择地址' }]}
              >
                <Select
                  placeholder={addrEditOptions.length ? '选择地址' : '该客户暂无地址簿条目'}
                  loading={addrEditLoading}
                  showSearch
                  optionFilterProp="label"
                  onChange={onAddressSelect}
                  options={addrEditOptions.map((a) => ({
                    value: a.id!,
                    label: a.remark ? `${a.label}（${a.remark}）` : a.label || a.id!,
                  }))}
                  disabled={!addrEditOptions.length}
                />
              </Form.Item>
            </Form>
            {addrEditDetail && (
              <div style={{ background: '#fafafa', borderRadius: 4, padding: 12, fontSize: 13, lineHeight: 1.8 }}>
                {addrEditDetail.contactName && <div>联系人：{addrEditDetail.contactName}</div>}
                {addrEditDetail.companyName && <div>公司：{addrEditDetail.companyName}</div>}
                {addrEditDetail.phone && <div>电话：{addrEditDetail.phone}</div>}
                {addrEditDetail.email && <div>邮箱：{addrEditDetail.email}</div>}
                {addrEditDetail.address && <div>地址：{addrEditDetail.address}</div>}
                {addrEditDetail.postalCode && <div>邮编：{addrEditDetail.postalCode}</div>}
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal
        title="编辑是否报关"
        open={!!requiresCustomsModal}
        onOk={onSaveRequiresCustoms}
        onCancel={() => {
          setRequiresCustomsModal(null);
          requiresCustomsForm.resetFields();
        }}
        confirmLoading={requiresCustomsSubmitting}
        destroyOnClose
      >
        {requiresCustomsModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{requiresCustomsModal.shipmentId}</strong>
            </p>
            <Form form={requiresCustomsForm} layout="vertical">
              <Form.Item name="requiresCustoms" label="是否报关" valuePropName="checked" rules={[{ required: true }]}>
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="编辑是否带电"
        open={!!containsBatteryModal}
        onOk={onSaveContainsBattery}
        onCancel={() => {
          setContainsBatteryModal(null);
          containsBatteryForm.resetFields();
        }}
        confirmLoading={containsBatterySubmitting}
        destroyOnClose
      >
        {containsBatteryModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{containsBatteryModal.shipmentId}</strong>
            </p>
            <Form form={containsBatteryForm} layout="vertical">
              <Form.Item name="containsBattery" label="是否带电" valuePropName="checked" rules={[{ required: true }]}>
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="编辑货物箱数"
        open={!!pieceCountModal}
        onOk={onSavePieceCount}
        onCancel={() => {
          setPieceCountModal(null);
          pieceCountForm.resetFields();
        }}
        confirmLoading={pieceCountSubmitting}
        destroyOnClose
      >
        {pieceCountModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{pieceCountModal.shipmentId}</strong>
            </p>
            <Form form={pieceCountForm} layout="vertical">
              <Form.Item
                name="declaredPieceCount"
                label="货物箱数"
                rules={[
                  { required: true, message: '请输入箱数' },
                  {
                    validator: async (_, v) => {
                      const n = Number(v);
                      if (!Number.isInteger(n) || n < 0) throw new Error('须为 0–999 的整数');
                      if (n > 999) throw new Error('不能超过 999');
                    },
                  },
                ]}
              >
                <InputNumber min={0} max={999} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      <Modal
        title="同步代理入仓数据"
        open={!!syncAgentDataModal}
        onOk={onSyncAgentDataOk}
        onCancel={() => setSyncAgentDataModal(null)}
        confirmLoading={syncAgentDataSubmitting}
      >
        {syncAgentDataModal && (
          <p>
            确定对运单 <strong>{syncAgentDataModal.shipmentId}</strong> 执行同步代理入仓数据？将从代理系统拉取入库状态、出库状态、测量数据，并更新运单及该运单下货物状态（不含轨迹）。若运单箱数与代理测量箱数不一致，将弹出确认框，可选择按代理数据更新。
          </p>
        )}
      </Modal>

      <Modal
        title="同步轨迹"
        open={!!syncTrackingDataModal}
        onOk={onSyncTrackingDataOk}
        onCancel={() => setSyncTrackingDataModal(null)}
        confirmLoading={syncTrackingDataSubmitting}
      >
        {syncTrackingDataModal && (
          <p>
            确定对运单 <strong>{syncTrackingDataModal.shipmentId}</strong> 执行同步轨迹？将从代理系统拉取运单轨迹并更新展示；若轨迹含运输/派送/签收语义，将前向更新运单状态（不可回退）。
          </p>
        )}
      </Modal>

      <Modal
        title="标识为问题件"
        open={!!markProblemTarget}
        zIndex={1100}
        onCancel={() => {
          setMarkProblemTarget(null);
          markProblemForm.resetFields();
        }}
        confirmLoading={markProblemSubmitting}
        onOk={() => {
          markProblemForm.validateFields().then((values) => {
            if (!markProblemTarget) return;
            const sid = markProblemTarget.shipmentId;
            setMarkProblemSubmitting(true);
            shipmentApi
              .markProblem(sid, values.content)
              .then(() => {
                message.success('已标识为问题件');
                setMarkProblemTarget(null);
                markProblemForm.resetFields();
                load();
                if (detailDrawerOpen && detailShipment?.shipmentId === sid) {
                  refreshDetailProblem();
                }
              })
              .catch((e) => message.error(e.response?.data?.message || '标识失败'))
              .finally(() => setMarkProblemSubmitting(false));
          });
        }}
      >
        {markProblemTarget && (
          <Form form={markProblemForm} layout="vertical">
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{markProblemTarget.shipmentId}</strong> 将标记为问题件，请填写首次说明。
            </p>
            <Form.Item
              name="content"
              label="首次说明"
              rules={[{ required: true, message: '请填写首次说明' }, { max: 2000, message: '最长 2000 字' }]}
            >
              <Input.TextArea rows={4} placeholder="如：过港查验 / 海关查验 / 丢货等" maxLength={2000} showCount />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="追加问题件进展"
        open={addProblemLogOpen}
        zIndex={1100}
        onCancel={() => {
          setAddProblemLogOpen(false);
          addProblemLogForm.resetFields();
        }}
        confirmLoading={addProblemLogSubmitting}
        onOk={() => {
          addProblemLogForm.validateFields().then((values) => {
            if (!detailShipment?.shipmentId) return;
            setAddProblemLogSubmitting(true);
            shipmentApi
              .addProblemLog(detailShipment.shipmentId, values.content)
              .then(() => {
                message.success('已追加进展');
                setAddProblemLogOpen(false);
                addProblemLogForm.resetFields();
                refreshDetailProblem();
                load();
              })
              .catch((e) => message.error(e.response?.data?.message || '追加失败'))
              .finally(() => setAddProblemLogSubmitting(false));
          });
        }}
      >
        <Form form={addProblemLogForm} layout="vertical">
          <Form.Item
            name="content"
            label="进展说明"
            rules={[{ required: true, message: '请填写进展说明' }, { max: 2000, message: '最长 2000 字' }]}
          >
            <Input.TextArea rows={4} maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="更新问题件状态"
        open={updateProblemStatusOpen}
        zIndex={1100}
        onCancel={() => {
          setUpdateProblemStatusOpen(false);
          updateProblemStatusForm.resetFields();
        }}
        confirmLoading={updateProblemStatusSubmitting}
        onOk={() => {
          updateProblemStatusForm.validateFields().then((values) => {
            if (!detailShipment?.shipmentId) return;
            setUpdateProblemStatusSubmitting(true);
            shipmentApi
              .updateProblemStatus(detailShipment.shipmentId, {
                problemStatus: values.problemStatus,
                content: values.content?.trim() || undefined,
              })
              .then(() => {
                message.success('问题件状态已更新');
                setUpdateProblemStatusOpen(false);
                updateProblemStatusForm.resetFields();
                refreshDetailProblem();
                load();
              })
              .catch((e) => message.error(e.response?.data?.message || '更新失败'))
              .finally(() => setUpdateProblemStatusSubmitting(false));
          });
        }}
      >
        <Form form={updateProblemStatusForm} layout="vertical">
          <Form.Item
            name="problemStatus"
            label="问题件状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select
              options={[
                { value: 'OPEN', label: '待处理' },
                { value: 'IN_PROGRESS', label: '处理中' },
              ]}
            />
          </Form.Item>
          <Form.Item name="content" label="说明（可选）" rules={[{ max: 2000, message: '最长 2000 字' }]}>
            <Input.TextArea rows={3} maxLength={2000} showCount placeholder="若填写将追加一条进展日志" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="解除问题件"
        open={resolveProblemOpen}
        zIndex={1100}
        onCancel={() => {
          setResolveProblemOpen(false);
          resolveProblemForm.resetFields();
        }}
        confirmLoading={resolveProblemSubmitting}
        okButtonProps={{ danger: true }}
        onOk={() => {
          resolveProblemForm.validateFields().then((values) => {
            if (!detailShipment?.shipmentId) return;
            setResolveProblemSubmitting(true);
            shipmentApi
              .resolveProblem(detailShipment.shipmentId, {
                content: values.content?.trim() || undefined,
                resolveReason: values.resolveReason,
              })
              .then(() => {
                message.success('已解除问题件');
                setResolveProblemOpen(false);
                resolveProblemForm.resetFields();
                refreshDetailProblem();
                load();
              })
              .catch((e) => message.error(e.response?.data?.message || '解除失败'))
              .finally(() => setResolveProblemSubmitting(false));
          });
        }}
      >
        <Form form={resolveProblemForm} layout="vertical">
          <p style={{ marginBottom: 12 }}>解除后列表行将恢复白底；历史进展日志仍保留。</p>
          {detailShipment?.problemMarkedSource === 'LLM' && (
            <Form.Item
              name="resolveReason"
              label="解除原因"
              rules={[{ required: true, message: '请选择解除原因' }]}
            >
              <Radio.Group>
                <Radio value="REAL">真实问题已解决</Radio>
                <Radio value="FALSE_POSITIVE">模型误判</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          <Form.Item name="content" label="结案说明（可选）" rules={[{ max: 2000, message: '最长 2000 字' }]}>
            <Input.TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <ResponsiveDrawer
        title={detailView === 'carton' ? '货物详情' : '运单详情'}
        size="xl"
        open={detailDrawerOpen}
        onClose={closeDetailDrawer}
        closable
      >
        {detailView === 'carton' ? (
          <CartonDetailDrawer
            embedded
            open={detailDrawerOpen && !!cartonDetailId}
            cartonId={cartonDetailId}
            onClose={backToShipmentDetail}
            onBack={backToShipmentDetail}
            onSaved={refreshDetailCartons}
          />
        ) : detailShipmentLoading ? (
          <Spin spinning />
        ) : detailShipment ? (
          <>
            {getShipmentStatus(detailShipment) === 'PRE_DECLARATION' && (
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <Button
                    type="primary"
                    loading={checkAgentOrderLoading}
                    onClick={() => openSubmitDepotModal(detailShipment)}
                  >
                    提交代理仓
                  </Button>
                  <Button danger onClick={() => openCancelShipment(detailShipment)}>
                    撤销
                  </Button>
                </Space>
              </div>
            )}
            {getShipmentStatus(detailShipment) === 'CANCELLED' && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="运单已撤销"
                description={[
                  detailShipment.cancelledAt ? `撤销时间：${dayjs(detailShipment.cancelledAt).format('YYYY-MM-DD HH:mm')}` : null,
                  detailShipment.cancelledBy ? `操作人：${detailShipment.cancelledBy}` : null,
                  detailShipment.cancelReason ? `原因：${detailShipment.cancelReason}` : null,
                ].filter(Boolean).join('；') || undefined}
                action={
                  <Button size="small" type="primary" onClick={() => openRedeclareShipment(detailShipment)}>
                    重新申报
                  </Button>
                }
              />
            )}
            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>基础信息</span>}
              extra={
                !isShipmentLocked(detailShipment) ? (
                  <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openUpdateLogistics(detailShipment)}>
                    更新运输方式
                  </Button>
                ) : null
              }
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={2} size="small">
                <Descriptions.Item label="运单号">{detailShipment.shipmentId}</Descriptions.Item>
                <Descriptions.Item label="目的国家">
                  <Space size={8}>
                    <span>
                      {detailShipment.destinationCountryName
                        || detailShipment.destinationCountry
                        || detailShipment.destinationCountryCode
                        || '-'}
                    </span>
                    {!isShipmentLocked(detailShipment) && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openCountryModal(detailShipment)}>
                        编辑
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="客户">{detailShipment.clientCode}</Descriptions.Item>
                <Descriptions.Item label="客户地址">
                  <Space size={8}>
                    <span>{formatClientAddr(detailShipment.clientAddr)}</span>
                    {getShipmentStatus(detailShipment) === 'PRE_DECLARATION' && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openAddressModal(detailShipment)}>
                        修改
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="箱数">
                  <Space size={8}>
                    <span>{detailShipment.cartonCount ?? 0}</span>
                    {!isShipmentLocked(detailShipment)
                      && !((detailShipment.boundCartonCount ?? 0) > 0)
                      && !((detailShipment.boundBatchCount ?? 0) > 0)
                      && !(detailCartons.length > 0)
                      && !(detailBatches.length > 0)
                      && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openPieceCountModal(detailShipment)}>
                        编辑
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="备注">
                  <Space size={8}>
                    <span>{detailShipment.remark?.trim() || '-'}</span>
                    {!isShipmentLocked(detailShipment) && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openRemarkModal(detailShipment)}>
                        编辑
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="代理">{detailShipment.agentIdentifier || '-'}</Descriptions.Item>
                <Descriptions.Item label="渠道">{channelDisplay(detailShipment)}</Descriptions.Item>
                <Descriptions.Item label="是否报关">
                  <Space size={8}>
                    <span>
                      {detailShipment.requiresCustoms !== false
                        ? `是（资料 ${detailShipment.customsDocCount ?? detailCustomsDocs.length}）`
                        : '否'}
                    </span>
                    {!isShipmentLocked(detailShipment) && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openRequiresCustomsModal(detailShipment)}>
                        编辑
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="是否带电">
                  <Space size={8}>
                    <span>{detailShipment.containsBattery === true ? '是' : '否'}</span>
                    {!isShipmentLocked(detailShipment) && (
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openContainsBatteryModal(detailShipment)}>
                        编辑
                      </Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="转单号">
                  <div>
                    <Space size={8}>
                      <span>{detailShipment.channelShipmentId?.trim() || '-'}</span>
                      {!isShipmentLocked(detailShipment) && (
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openChannelShipmentModal(detailShipment)}>
                          编辑
                        </Button>
                      )}
                    </Space>
                    {detailShipment.channelShipmentTrackingUrl ? (
                      <div>
                        <CarrierTrackingLink shipment={detailShipment} logoHeight={18} />
                      </div>
                    ) : (
                      detailShipment.channelShipmentTrackingHint && (
                        <div>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {detailShipment.channelShipmentTrackingHint}
                          </Typography.Text>
                        </div>
                      )
                    )}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="船名航次">
                  <div>
                    <span>
                      {detailShipment.vesselName?.trim()
                        ? detailShipment.vesselVoyage?.trim()
                          ? `${detailShipment.vesselName.trim()} ${detailShipment.vesselVoyage.trim()}`
                          : detailShipment.vesselName.trim()
                        : '-'}
                    </span>
                    {detailShipment.vesselImo && (
                      <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        IMO {detailShipment.vesselImo}
                      </Typography.Text>
                    )}
                    {!detailShipment.channelShipmentId?.trim() && detailShipment.channelShipmentTrackingUrl && (
                      <div>
                        <CarrierTrackingLink shipment={detailShipment} logoHeight={18} />
                      </div>
                    )}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="代理入库单号">{detailShipment.agentInboundNo || '-'}</Descriptions.Item>
                <Descriptions.Item label="运输方式">
                  <Space size={4}>
                    <span>
                      {transportModes.find((t) => t.mode === detailShipment.transportMode)?.displayName
                        || detailShipment.transportMode
                        || '-'}
                    </span>
                    {!isShipmentLocked(detailShipment) && (
                      <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        style={{ padding: 0 }}
                        onClick={() => openUpdateLogistics(detailShipment)}
                      />
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="代理订单来源">
                  {detailShipment.agentOrderOrigin === 'SYNCED_FROM_AGENT'
                    ? '从代理同步'
                    : detailShipment.agentOrderOrigin === 'CREATED_IN_KFY'
                      ? '凯飞越创建提交'
                      : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="状态" span={2}>
                  {statusLabel[getShipmentStatus(detailShipment)] || detailShipment.status}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              size="small"
              title={
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  绑定的货物批次（{detailBatches.length} 批 / {detailShipment.cartonCount ?? 0} 件）
                </span>
              }
              style={{ marginBottom: 16 }}
              extra={
                <Space>
                  {getShipmentStatus(detailShipment) !== 'CANCELLED' && detailBatches.length > 0 && (
                    <Button
                      size="small"
                      type="primary"
                      loading={packingListGenerating}
                      onClick={() => onGeneratePackingList(detailShipment.shipmentId)}
                    >
                      生成随货清单
                    </Button>
                  )}
                  {getShipmentStatus(detailShipment) === 'PRE_DECLARATION' && (
                    <>
                      <Button size="small" onClick={() => setBatchFormShipment(detailShipment)}>
                        新增批次
                      </Button>
                      <Button type="primary" size="small" onClick={() => openBindModal(detailShipment)}>
                        绑定批次
                      </Button>
                    </>
                  )}
                </Space>
              }
            >
              {detailShipment.cargoSyncPending && detailBatches.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="批次件数与代理入仓箱数不一致"
                  description={
                    <div>
                      <p style={{ marginBottom: 8 }}>
                        运单件数 {detailShipment.cargoSyncKfyCount ?? detailShipment.cartonCount ?? '-'}，代理箱数{' '}
                        {detailShipment.cargoSyncAgentCount ?? '-'}，请将代理箱匹配到批次：
                      </p>
                      <Space wrap>
                        <Button
                          type="primary"
                          onClick={() => setMatchModal({ shipmentId: detailShipment.shipmentId })}
                        >
                          匹配到批次
                        </Button>
                        <Button
                          loading={resolveCargoSyncLoading}
                          onClick={() => resolveCargoSync('LATER')}
                        >
                          稍后手动处理
                        </Button>
                      </Space>
                    </div>
                  }
                />
              )}
              {detailShipment.cargoSyncPending && detailBatches.length === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="绑定箱数与代理测量箱数不一致"
                  description={
                    <div>
                      <p style={{ marginBottom: 8 }}>
                        运单箱数 {detailShipment.cargoSyncKfyCount ?? detailCartons.length}，代理箱数{' '}
                        {detailShipment.cargoSyncAgentCount ?? '-'}，请选择处理方式：
                      </p>
                      <Space wrap style={{ marginBottom: 8 }}>
                        <Button
                          type="primary"
                          danger
                          loading={resolveCargoSyncLoading}
                          onClick={() => {
                            Modal.confirm({
                              title: '确认替换？',
                              content: '将解绑当前运单下全部货物，再按代理测量清单新建或复用箱号并绑定。',
                              okText: '替换',
                              okButtonProps: { danger: true },
                              onOk: () => resolveCargoSync('REPLACE'),
                            });
                          }}
                        >
                          替换
                        </Button>
                        <Button
                          type="primary"
                          loading={resolveCargoSyncLoading}
                          onClick={() => {
                            Modal.confirm({
                              title: '确认合并？',
                              content: '保留已绑定货物，仅将代理侧尚不存在的箱号新建并绑定到本运单。',
                              okText: '合并',
                              onOk: () => resolveCargoSync('MERGE'),
                            });
                          }}
                        >
                          合并
                        </Button>
                        <Button
                          loading={resolveCargoSyncLoading}
                          onClick={() => resolveCargoSync('LATER')}
                        >
                          稍后手动处理
                        </Button>
                      </Space>
                      {detailShipment.agentMeasurementDataJson && (
                        <Collapse
                          size="small"
                          items={[
                            {
                              key: 'agent-cargo',
                              label: '查看代理测量箱清单',
                              children: (
                                <AgentMeasurementTable json={detailShipment.agentMeasurementDataJson} />
                              ),
                            },
                          ]}
                        />
                      )}
                    </div>
                  }
                />
              )}
              <Spin spinning={detailCartonsLoading}>
              {(() => {
                const cartonColumns = [
                  { title: '货物标识', dataIndex: 'cartonId', width: 160, ellipsis: true },
                  { title: '客户', dataIndex: 'belongClientId', width: 120, render: (v: string) => customers.find((c) => c.id === v || c.customerCode === v)?.name || v || '-' },
                  { title: '状态', dataIndex: 'state', width: 100, render: (v: string) => cartonStateLabel[v || ''] || v || '-' },
                ];
                const cartonRow = (record: CartonDTO) => ({
                  onClick: () => {
                    if (record.id) openCartonInDetail(record.id);
                  },
                  style: { cursor: 'pointer' },
                });
                const batchIdSet = new Set(detailBatches.map((b) => b.batchId));
                const looseCartons = detailCartons.filter((c) => !c.batchId || !batchIdSet.has(c.batchId));
                const canEdit = getShipmentStatus(detailShipment) === 'PRE_DECLARATION';
                const canPackingList = getShipmentStatus(detailShipment) !== 'CANCELLED';
                return (
                  <>
                    <AdaptiveTable<CartonBatchDTO>
                      dataSource={detailBatches}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      locale={{ emptyText: '暂无绑定批次' }}
                      expandable={{
                        rowExpandable: (b) => detailCartons.some((c) => c.batchId === b.batchId),
                        expandedRowRender: (b) => (
                          <AdaptiveTable<CartonDTO>
                            dataSource={detailCartons.filter((c) => c.batchId === b.batchId)}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            onRow={cartonRow}
                            columns={cartonColumns}
                          />
                        ),
                      }}
                      columns={[
                        { title: '批次标识', dataIndex: 'batchId', width: 160, ellipsis: true },
                        { title: '件数', dataIndex: 'pieceCount', width: 70, render: (v?: number) => v ?? '-' },
                        { title: '已生成单箱', dataIndex: 'materializedCount', width: 100, render: (v?: number) => v ?? 0 },
                        { title: '货物品名', dataIndex: 'cargoName', width: 140, ellipsis: true, render: (v?: string) => v || '-' },
                        { title: '备注', dataIndex: 'batchRemark', ellipsis: true, render: (v?: string) => v || '-' },
                        ...(canEdit || canPackingList
                          ? [{
                            title: '操作',
                            key: 'action',
                            width: canEdit ? 160 : 110,
                            render: (_: unknown, b: CartonBatchDTO) => (
                              <Space size={4}>
                                {canPackingList && b.batchId && (
                                  <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0 }}
                                    loading={packingListBatchGeneratingId === b.batchId}
                                    onClick={() => onGenerateBatchPackingList(detailShipment.shipmentId, b.batchId!)}
                                  >
                                    生成随货清单
                                  </Button>
                                )}
                                {canEdit && (
                                  <Popconfirm
                                    title={`解绑批次 ${b.batchId}？`}
                                    onConfirm={() => unbindOneBatch(detailShipment.shipmentId, b)}
                                  >
                                    <Button type="link" size="small" danger>解绑</Button>
                                  </Popconfirm>
                                )}
                              </Space>
                            ),
                          }]
                          : []),
                      ]}
                    />
                    {looseCartons.length > 0 && (
                      <>
                        <div style={{ margin: '12px 0 8px', color: '#666' }}>未归属批次的单箱（{looseCartons.length} 箱）</div>
                        <AdaptiveTable<CartonDTO>
                          dataSource={looseCartons}
                          rowKey="id"
                          size="small"
                          pagination={false}
                          onRow={cartonRow}
                          columns={cartonColumns}
                        />
                      </>
                    )}
                  </>
                );
              })()}
              </Spin>
              <Collapse
                size="small"
                style={{ marginTop: 12 }}
                items={[
                  {
                    key: 'packing-lists',
                    label: `随货清单记录（${detailPackingLists.length}）`,
                    children: (
                      <Spin spinning={detailPackingListsLoading}>
                        {detailPackingLists.length === 0 ? (
                          <div style={{ color: '#999' }}>暂无随货清单记录</div>
                        ) : (
                          <AdaptiveTable
                            dataSource={detailPackingLists}
                            rowKey="id"
                            size="small"
                            pagination={false}
                            columns={[
                              {
                                title: '生成时间',
                                dataIndex: 'generatedAt',
                                width: 160,
                                render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
                              },
                              {
                                title: '范围',
                                width: 140,
                                render: (_: unknown, r: ShipmentPackingListDTO) =>
                                  r.scope === 'BATCH' ? `批次 ${r.batchId || '-'}` : '整单',
                              },
                              { title: '文件名', dataIndex: 'fileName', ellipsis: true },
                              {
                                title: '操作人',
                                width: 100,
                                render: (_: unknown, r: ShipmentPackingListDTO) =>
                                  r.generatedByName || r.generatedBy || '-',
                              },
                              {
                                title: '操作',
                                width: 80,
                                render: (_: unknown, r: ShipmentPackingListDTO) => (
                                  <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0 }}
                                    onClick={() => onDownloadPackingList(detailShipment.shipmentId, r)}
                                  >
                                    下载
                                  </Button>
                                ),
                              },
                            ]}
                          />
                        )}
                      </Spin>
                    ),
                  },
                ]}
              />
            </Card>

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>问题件</span>}
              extra={
                detailShipment.problemActive === true ? (
                  <NarrowExtra size={8}>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={() => {
                        addProblemLogForm.resetFields();
                        setAddProblemLogOpen(true);
                      }}
                    >
                      追加进展
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0 }}
                      onClick={() => {
                        updateProblemStatusForm.setFieldsValue({
                          problemStatus: (detailShipment.problemStatus === 'IN_PROGRESS' ? 'IN_PROGRESS' : 'OPEN') as 'OPEN' | 'IN_PROGRESS',
                          content: undefined,
                        });
                        setUpdateProblemStatusOpen(true);
                      }}
                    >
                      更新状态
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      danger
                      style={{ padding: 0 }}
                      onClick={() => {
                        resolveProblemForm.resetFields();
                        setResolveProblemOpen(true);
                      }}
                    >
                      解除问题件
                    </Button>
                  </NarrowExtra>
                ) : getShipmentStatus(detailShipment) !== 'CANCELLED' ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => {
                      markProblemForm.resetFields();
                      setMarkProblemTarget(detailShipment);
                    }}
                  >
                    标识为问题件
                  </Button>
                ) : null
              }
              style={{
                marginBottom: 16,
                background: detailShipment.problemActive === true ? '#fff1f0' : undefined,
              }}
            >
              {detailShipment.problemActive === true ? (
                <>
                  <Descriptions column={2} size="small" style={{ marginBottom: 12 }}>
                    <Descriptions.Item label="问题状态">
                      <Space size={4}>
                        <Tag color="error">
                          {problemStatusLabel[detailShipment.problemStatus || ''] || detailShipment.problemStatus || '待处理'}
                        </Tag>
                        {detailShipment.problemMarkedSource === 'LLM' && <Tag>自动</Tag>}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="标记时间">
                      {detailShipment.problemMarkedAt
                        ? dayjs(detailShipment.problemMarkedAt).format('YYYY-MM-DD HH:mm')
                        : '-'}
                    </Descriptions.Item>
                  </Descriptions>
                  <Spin spinning={detailProblemLogsLoading}>
                    {detailProblemLogs.length === 0 ? (
                      <div style={{ color: '#999' }}>暂无进展日志</div>
                    ) : (
                      <Timeline
                        items={detailProblemLogs.map((log) => ({
                          children: (
                            <div>
                              <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                                {log.createdAt ? dayjs(log.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
                                {log.createdBy ? ` · ${log.createdBy}` : ''}
                                {log.relatedAction ? ` · ${log.relatedAction}` : ''}
                              </div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{log.content}</div>
                            </div>
                          ),
                        }))}
                      />
                    )}
                  </Spin>
                </>
              ) : (
                <div style={{ color: '#666' }}>
                  当前非问题件
                  {detailProblemLogs.length > 0 && (
                    <Collapse
                      ghost
                      style={{ marginTop: 8 }}
                      items={[
                        {
                          key: 'history',
                          label: `历史进展（${detailProblemLogs.length}）`,
                          children: (
                            <Timeline
                              items={detailProblemLogs.map((log) => ({
                                children: (
                                  <div>
                                    <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                                      {log.createdAt ? dayjs(log.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
                                      {log.createdBy ? ` · ${log.createdBy}` : ''}
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{log.content}</div>
                                  </div>
                                ),
                              }))}
                            />
                          ),
                        },
                      ]}
                    />
                  )}
                </div>
              )}
            </Card>

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>计费重</span>}
              extra={
                <NarrowExtra>
                  <Button
                    size="small"
                    onClick={() => onCalculateBillingWeight(detailShipment)}
                    disabled={!detailShipment.hasBillingRule || getShipmentStatus(detailShipment) === 'CANCELLED'}
                  >
                    计算计费重
                  </Button>
                  {(detailShipment.status === 'SUBMITTED_TO_DEPOT' ||
                    detailShipment.status === 'HANDOVER_CONFIRMING') && (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setWeightModal(detailShipment)}
                    >
                      确认重量
                    </Button>
                  )}
                </NarrowExtra>
              }
              style={{ marginBottom: 16 }}
            >
              {!detailShipment.hasBillingRule && (
                <Alert
                  type="warning"
                  showIcon
                  message="渠道未关联计费重计算规则，确认重量时请手动输入"
                  style={{ marginBottom: 12 }}
                />
              )}
              {detailShipment.billingCalcStale && (
                <Alert
                  type="warning"
                  showIcon
                  message={
                    detailShipment.billingCalcStaleReason === 'MEASUREMENT_CHANGED'
                      ? '测量数据已更新，计费重需重新计算后再确认'
                      : '规则已变更，建议重新计算后再次确认'
                  }
                  style={{ marginBottom: 12 }}
                />
              )}
              {detailShipment.billingCalcMissingInput && (
                <Alert
                  type="info"
                  showIcon
                  message="缺少入仓箱测量数据，无法自动计算"
                  style={{ marginBottom: 12 }}
                />
              )}
              <Descriptions column={2} size="small" bordered>
                <Descriptions.Item label="计费规则">
                  {detailShipment.billingRuleName || (detailShipment.hasBillingRule ? '-' : '未关联')}
                </Descriptions.Item>
                <Descriptions.Item label="公式版本">
                  {detailShipment.billingCalcFormulaVersion ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="建议计费重(kg)">
                  {detailShipment.billingCalcSuggestedKg ?? detailShipment.pendingWeight ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="已确认代理计费重(kg)">
                  {detailShipment.confirmedWeight ?? '-'}
                </Descriptions.Item>
                {detailShipment.billingRuleText && (
                  <Descriptions.Item label="规则正文" span={2}>
                    <Typography.Paragraph ellipsis={{ rows: 2, expandable: true }} style={{ marginBottom: 0 }}>
                      {detailShipment.billingRuleText}
                    </Typography.Paragraph>
                  </Descriptions.Item>
                )}
              </Descriptions>
              {parseBillingProcessSteps(detailShipment.billingCalcProcessJson).length > 0 && (
                <Collapse
                  style={{ marginTop: 12 }}
                  items={[
                    {
                      key: 'process',
                      label: '计费重计算过程',
                      children: (
                        <ol style={{ margin: 0, paddingLeft: 18 }}>
                          {parseBillingProcessSteps(detailShipment.billingCalcProcessJson).map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                      ),
                    },
                  ]}
                />
              )}
              <WeightConfirmationHistory shipmentId={detailShipment.shipmentId} refreshKey={weightHistoryKey} />
            </Card>

            <Card
              size="small"
              title={
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  入仓数据（{parseAgentMeasurementJson(detailShipment.agentMeasurementDataJson).rows.length}箱）
                </span>
              }
              extra={
                <Space size={8}>
                  {detailShipment.agentMeasurementMode === 'MANUAL' && (
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      disabled={!detailShipment.agentInboundNo?.trim() || isShipmentLocked(detailShipment)}
                      onClick={() => setManualMeasurementOpen(true)}
                    >
                      上传测量数据
                    </Button>
                  )}
                  <Button
                    type="primary"
                    size="small"
                    loading={syncAgentDataFromDrawerLoading}
                    disabled={!detailShipment.agentInboundNo?.trim() || getShipmentStatus(detailShipment) === 'CANCELLED'}
                    onClick={syncAgentDataFromDrawer}
                  >
                    同步代理入仓数据
                  </Button>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
                <Descriptions.Item label="测量数据来源" span={2}>
                  {detailShipment.agentMeasurementSource === 'MANUAL_LLM' ? (
                    <Space size={4} wrap>
                      <Tag color="purple">手动上传</Tag>
                      {detailShipment.manualMeasurementConfirmedAt && (
                        <span>{dayjs(detailShipment.manualMeasurementConfirmedAt).format('YYYY-MM-DD HH:mm')}</span>
                      )}
                      {detailShipment.manualMeasurementConfirmedBy && (
                        <span>· {detailShipment.manualMeasurementConfirmedBy}</span>
                      )}
                    </Space>
                  ) : detailShipment.agentMeasurementSource === 'API' ? (
                    <Tag color="blue">API 同步</Tag>
                  ) : detailShipment.agentMeasurementMode === 'MANUAL' ? (
                    <span style={{ color: '#999' }}>该代理需手动上传测量数据，尚未上传</span>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="代理入库状态">
                  {detailShipment.agentInboundStatus != null
                    ? agentInboundStatusLabel[detailShipment.agentInboundStatus] ?? String(detailShipment.agentInboundStatus)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理出库状态">
                  {detailShipment.agentOutboundStatus != null
                    ? agentOutboundStatusLabel[detailShipment.agentOutboundStatus] ?? String(detailShipment.agentOutboundStatus)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理测量重量（毛重）">
                  {detailShipment.agentMeasuredWeightKg != null && detailShipment.agentMeasuredWeightKg > 0
                    ? `${detailShipment.agentMeasuredWeightKg} kg`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理测量体积">
                  {detailShipment.agentMeasuredVolumeCbm != null && detailShipment.agentMeasuredVolumeCbm > 0
                    ? `${detailShipment.agentMeasuredVolumeCbm} m³`
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>代理测量数据</div>
              <AgentMeasurementTable json={detailShipment.agentMeasurementDataJson} />
              {detailShipment.agentMeasurementMode === 'MANUAL' && (
                <ManualMeasurementHistory
                  shipmentId={detailShipment.shipmentId}
                  refreshKey={manualMeasurementRefreshKey}
                />
              )}
              <ManualMeasurementModal
                open={manualMeasurementOpen}
                shipmentId={detailShipment.shipmentId}
                onClose={() => setManualMeasurementOpen(false)}
                onConfirmed={(res) => onManualMeasurementConfirmed(detailShipment.shipmentId, res)}
              />
            </Card>

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>轨迹数据</span>}
              extra={
                narrow ? (
                  <NarrowExtra>
                    <span style={{ fontSize: 12, color: '#666' }}>
                      手动轨迹{' '}
                      <Switch
                        size="small"
                        checked={!!detailShipment.manualTrackingEnabled}
                        loading={manualTrackingSwitching}
                        disabled={isShipmentLocked(detailShipment)}
                        onChange={onManualTrackingSwitch}
                      />
                    </span>
                    {detailShipment.manualTrackingEnabled && canManualConfirmDelivery(detailShipment) && (
                      <Button onClick={() => setManualTrackOpen(true)}>更新轨迹</Button>
                    )}
                    {canManualConfirmDelivery(detailShipment) && (
                      <Button onClick={() => setManualConfirmDeliveryOpen(true)}>确认签收</Button>
                    )}
                  </NarrowExtra>
                ) : (
                <Space>
                  <span style={{ fontSize: 12, color: '#666' }}>手动轨迹</span>
                  <Switch
                    size="small"
                    checked={!!detailShipment.manualTrackingEnabled}
                    loading={manualTrackingSwitching}
                    disabled={isShipmentLocked(detailShipment)}
                    onChange={onManualTrackingSwitch}
                  />
                  {detailShipment.manualTrackingEnabled && canManualConfirmDelivery(detailShipment) && (
                    <Button size="small" onClick={() => setManualTrackOpen(true)}>
                      更新轨迹
                    </Button>
                  )}
                  {canManualConfirmDelivery(detailShipment) && (
                    <Button size="small" onClick={() => setManualConfirmDeliveryOpen(true)}>
                      确认签收
                    </Button>
                  )}
                  <Tooltip title={detailShipment.manualTrackingEnabled ? '已开启手动轨迹，不同步代理' : undefined}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        loading={syncTrackingDataFromDrawerLoading}
                        disabled={
                          !!detailShipment.manualTrackingEnabled
                          || !detailShipment.agentInboundNo?.trim()
                          || isShipmentLocked(detailShipment)
                        }
                        onClick={syncTrackingDataFromDrawer}
                      >
                        同步轨迹
                      </Button>
                    </span>
                  </Tooltip>
                </Space>
                )
              }
              style={{ marginBottom: 16 }}
            >
              <TrackingDisplayPanel
                data={detailTrackingDisplay}
                fallbackEvents={detailShipment.agentTrackingLatest ?? undefined}
                loading={detailTrackingDisplayLoading}
                shipmentId={detailShipment.shipmentId}
              />
              {narrow && (
                <Tooltip title={detailShipment.manualTrackingEnabled ? '已开启手动轨迹，不同步代理' : undefined}>
                  <span style={{ display: 'block', marginTop: 12 }}>
                    <Button
                      type="primary"
                      block
                      style={{ height: 40 }}
                      loading={syncTrackingDataFromDrawerLoading}
                      disabled={
                        !!detailShipment.manualTrackingEnabled
                        || !detailShipment.agentInboundNo?.trim()
                        || isShipmentLocked(detailShipment)
                      }
                      onClick={syncTrackingDataFromDrawer}
                    >
                      同步轨迹
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Card>
            <ManualConfirmDeliveryModal
              shipment={manualConfirmDeliveryOpen ? detailShipment : null}
              onClose={() => setManualConfirmDeliveryOpen(false)}
              onConfirmed={onManualConfirmDelivery}
            />
            <ManualTrackEventModal
              shipment={manualTrackOpen ? detailShipment : null}
              onClose={() => setManualTrackOpen(false)}
              onSubmitted={onManualTrackSubmitted}
            />

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>报关资料</span>}
              extra={
                !isShipmentLocked(detailShipment) ? (
                  <Upload
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    showUploadList={false}
                    beforeUpload={onUploadCustomsDoc}
                    disabled={customsDocUploading}
                  >
                    <Button type="primary" size="small" icon={<UploadOutlined />} loading={customsDocUploading}>
                      上传
                    </Button>
                  </Upload>
                ) : null
              }
              style={{ marginBottom: 16 }}
            >
              {detailShipment.requiresCustoms === false && (
                <Alert type="info" showIcon style={{ marginBottom: 12 }} message="当前不需要报关，交仓不校验资料；已上传文件仍保留。" />
              )}
              {detailShipment.requiresCustoms !== false && detailCustomsDocs.length === 0 && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="需要报关但尚未上传资料；提交代理仓时会提示确认。" />
              )}
              <Spin spinning={detailCustomsDocsLoading}>
                {detailCustomsDocs.length === 0 ? (
                  <div style={{ color: '#999' }}>暂无报关资料</div>
                ) : (
                  <AdaptiveTable
                    dataSource={detailCustomsDocs}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={[
                      { title: '文件名', dataIndex: 'fileName', ellipsis: true, render: (v: string) => v || '-' },
                      {
                        title: '上传时间',
                        dataIndex: 'createdAt',
                        width: 160,
                        render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
                      },
                      {
                        title: '操作',
                        width: 140,
                        render: (_: unknown, doc: ShipmentCustomsDocDTO) => (
                          <Space size={4}>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onDownloadCustomsDoc(doc)}>
                              下载
                            </Button>
                            {!isShipmentLocked(detailShipment) && (
                              <Button type="link" size="small" danger style={{ padding: 0 }} onClick={() => onDeleteCustomsDoc(doc)}>
                                删除
                              </Button>
                            )}
                          </Space>
                        ),
                      },
                    ]}
                  />
                )}
              </Spin>
            </Card>

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>发票</span>}
              style={{ marginBottom: 16 }}
              extra={
                getShipmentStatus(detailShipment) !== 'CANCELLED' ? (
                  <Upload
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                    showUploadList={false}
                    beforeUpload={onUploadInvoice}
                    disabled={invoiceUploading}
                  >
                    <Button type="primary" size="small" icon={<UploadOutlined />} loading={invoiceUploading}>
                      上传发票
                    </Button>
                  </Upload>
                ) : null
              }
            >
              <Spin spinning={detailInvoicesLoading}>
              {detailInvoices.length === 0 ? (
                <div style={{ color: '#999' }}>暂无发票记录，请上传发票</div>
              ) : (
                <AdaptiveTable
                  dataSource={detailInvoices}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '文件名', dataIndex: 'fileName', ellipsis: true, render: (v: string) => v || '-' },
                    {
                      title: '来源',
                      dataIndex: 'source',
                      width: 72,
                      render: (v: string) => (v === 'UPLOADED' ? '上传' : v === 'GENERATED' ? '生成' : '生成'),
                    },
                    {
                      title: '状态',
                      dataIndex: 'validity',
                      width: 80,
                      render: (v: string) => {
                        if (v === 'VALID') return <Tag color="success">有效</Tag>;
                        if (v === 'INVALID') return <Tag>无效</Tag>;
                        return <Tag color="default">未确认</Tag>;
                      },
                    },
                    {
                      title: '时间',
                      dataIndex: 'generatedAt',
                      width: 170,
                      render: (v: string) => (v ? new Date(v).toLocaleString() : '-'),
                    },
                    {
                      title: '上传人',
                      dataIndex: 'uploadedBy',
                      width: 100,
                      ellipsis: true,
                      render: (v: string) => v || '-',
                    },
                    {
                      title: '操作',
                      width: 168,
                      render: (_: unknown, inv: ShipmentInvoiceDTO) => (
                        <Space size={4} wrap>
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (detailShipment?.shipmentId) onDownloadInvoice(detailShipment.shipmentId, inv);
                            }}
                          >
                            下载
                          </Button>
                          {inv.validity !== 'VALID' && (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                              loading={invoiceConfirmingId === inv.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                onConfirmValidInvoice(inv);
                              }}
                            >
                              确认为有效发票
                            </Button>
                          )}
                        </Space>
                      ),
                    },
                  ]}
                />
              )}
              </Spin>
            </Card>
          </>
        ) : null}
      </ResponsiveDrawer>
    </>
  );
};

export default ShipmentList;
